/**
 * The words of the Employee Performance, Points & Rewards, Work & Productivity, Projects & Clients, Dashboard
 * Access and Dashboard Settings pages, in English and Arabic. Every key here needs its Arabic: `areaBAr` is
 * typed on `areaBEn`. The Arabic keeps the admin's own words for the same things (مهام الطابور, مهلة الاطلاع,
 * مكافآت الأيام…), and counts are written "label: n" where Arabic would otherwise need number agreement.
 */

export const areaBEn = {
  /* ---------------------------------------------------------------- Employee Performance ---- */
  'employees.title': 'Employee Performance',
  'employees.subtitle': 'Each person’s work score for the period, from the evidence of their tasks, workflow steps, Q Tasks and tickets.',
  'employees.allDepartments': 'All departments',
  'employees.searchPlaceholder': 'Search by name',
  'employees.sortLabel': 'Order within each department',
  'employees.sort.name': 'Name',
  'employees.sort.score': 'Score, highest first',
  'employees.sort.evidence': 'Evidence, most first',
  'employees.kpi.people': 'People',
  'employees.kpi.peopleHint': 'Active employees, and anyone with work evidence in the period',
  'employees.kpi.scored': 'With a score',
  'employees.kpi.scoredHint': 'Enough evidence to be scored',
  'employees.kpi.notEnough': 'Not enough evidence',
  'employees.kpi.notEnoughHint': 'No score — never counted as 0',
  'employees.kpi.coverage': 'Coverage',
  'employees.kpi.coverageHint': 'The share of people with a score',
  'employees.kpi.median': 'Median score',
  'employees.kpi.medianHint': 'Of the people with a score',
  'employees.kpi.medianNone': 'Nobody has a score yet',
  'employees.byLabel': 'Scores by label',
  'employees.byLabelHint': 'People with a score only.',
  'employees.label.EXCELLENT': 'Excellent',
  'employees.label.VERY_GOOD': 'Very good',
  'employees.label.GOOD': 'Good',
  'employees.label.NEEDS_ATTENTION': 'Needs attention',
  'employees.method.title': 'How the score is made',
  'employees.method.basis':
    'Five dimensions — completion, deadlines, quality, tickets and collaboration — each a ratio of the person’s own work evidence: the tasks they delivered, their workflow steps, Q Tasks and tickets. The score is the weighted mean of the dimensions that could be measured.',
  'employees.method.excluded': 'Nothing else counts: no attendance, shifts, Free Shifts, leave, idle time, pay or wallet points — and no AI.',
  'employees.method.nullScore': 'Too little evidence, or too little of the weight measurable, gives no score at all — “Not enough evidence” — never a forced 0 or 100.',
  'employees.method.weights':
    'The weights are the dashboard settings in force on the period’s first day (version {version}). A department may have its own weights, and a dimension may be switched off:',
  'employees.tableTitle': 'Employees by department',
  'employees.notCompared': 'Scores are read within a department, never compared across departments: departments do different work.',
  'employees.noDepartment': 'No department',
  'employees.groupCount': '{n} in this department',
  'employees.empty': 'No employees for this period',
  'employees.emptyHint': 'Nobody matches — try another department, name or period.',
  'employees.col.departments': 'Departments',
  'employees.col.score': 'Score',
  'employees.col.confidence': 'Confidence',
  'employees.col.evidence': 'Evidence',
  'employees.col.measurable': 'Measurable weight',
  'employees.col.tasks': 'Tasks',
  'employees.col.steps': 'Workflow steps',
  'employees.col.qTasks': 'Q Tasks',
  'employees.col.tickets': 'Tickets',
  'employees.col.missed': 'Missed',
  'employees.confidence.HIGH': 'High',
  'employees.confidence.MEDIUM': 'Medium',
  'employees.confidence.LOW': 'Low',
  'employees.confidence.NONE': 'None',
  'employees.reason.INSUFFICIENT_EVIDENCE': 'Too few work events',
  'employees.reason.INSUFFICIENT_COVERAGE': 'Too little of the weight measurable',
  'employees.reason.INSUFFICIENT_EVIDENCE.long': 'Fewer work events than the settings ask for before a number is shown.',
  'employees.reason.INSUFFICIENT_COVERAGE.long': 'Too little of the dimensions’ weight had evidence to divide — the rest could not be measured.',
  'employees.revision': 'Revision {n}',

  /* ---------------------------------------------------------------- One employee ------------ */
  'employee.back': 'All employees',
  'employee.primaryDepartment': 'Primary department',
  'employee.tab.performance': 'Performance',
  'employee.tab.evidence': 'Evidence',
  'employee.tab.points': 'Points',
  'employee.noRow': 'No evaluation for this period',
  'employee.noRowHint': 'They had not joined by the end of this period.',
  'employee.score.title': 'Work score',
  'employee.score.subtitle': 'Out of 100, from the work evidence of the period',
  'employee.score.revision': 'Revision',
  'employee.score.notStored': 'Not stored — computed now',
  'employee.score.settingsVersion': 'Settings version',
  'employee.score.labels': 'Labels: Excellent from {excellent}, Very good from {veryGood}, Good from {good}; below {good}, Needs attention.',
  'employee.score.confidenceHint': 'Confidence is how much evidence there was and how much of the weight it covered — never a model’s certainty.',
  'employee.previous.title': 'The period before',
  'employee.previous.none': 'No evaluation for the period before.',
  'employee.previous.change': 'Change in score: {value}',
  'employee.dimensions.title': 'Dimensions',
  'employee.dimensions.subtitle': 'Each a ratio of the person’s own evidence. One with nothing to divide is not measurable and carries no weight.',
  'employee.dimensions.dimension': 'Dimension',
  'employee.dimensions.weight': 'Weight',
  'employee.dimensions.ratio': 'Ratio',
  'employee.dimensions.score': 'Score',
  'employee.dimensions.formula': 'How it is measured',
  'employee.dimensions.off': 'Switched off',
  'employee.dimensions.notMeasurable': 'Not measurable — nothing to divide',
  'employee.dimensions.note': 'The score is the weighted mean of the measurable dimensions, their weights scaled to the part that was measured.',
  'employee.metrics.title': 'Counts',
  'employee.metrics.subtitle': 'What the person’s evidence adds up to in the period.',
  'employee.metrics.delivered': 'Delivered',
  'employee.metrics.deadlines': 'Deadlines',
  'employee.metrics.review': 'Review of their work',
  'employee.metrics.tickets': 'Tickets',
  'employee.metrics.collaboration': 'Reviews they did',
  'employee.metric.tasksDelivered': 'Tasks delivered',
  'employee.metric.workflowSteps': 'Workflow steps finished',
  'employee.metric.qTasksDone': 'Q Tasks done',
  'employee.metric.ticketsResolved': 'Tickets resolved',
  'employee.metric.ticketsReopened': 'Resolved, then reopened',
  'employee.metric.finishedOnTime': 'Finished on time',
  'employee.metric.finishedLate': 'Finished late',
  'employee.metric.dueMissed': 'Deadlines missed',
  'employee.metric.approved': 'Approved by a reviewer',
  'employee.metric.returned': 'Sent back for rework',
  'employee.metric.awaitingReview': 'Awaiting review',
  'employee.metric.selfCompleted': 'Done with no review',
  'employee.metric.viewSlaOnTime': 'Tickets viewed in time',
  'employee.metric.viewSlaLate': 'Tickets not viewed in time',
  'employee.metric.reviewsDone': 'Reviews done',
  'employee.metric.reviewsDoneLate': 'Reviews done late',
  'employee.metric.reviewsOverdue': 'Reviews still waiting',
  'employee.metric.blocked': 'Blocked work',
  'employee.metric.blockedHint': 'Context — never scored',
  'employee.findings.title': 'Findings',
  'employee.findings.subtitle': 'What the evidence shows — each one counted from the records, not written by AI.',
  'employee.findings.tone.POSITIVE': 'Went well',
  'employee.findings.tone.CAUTION': 'To look at',
  'employee.findings.tone.UNCERTAIN': 'Uncertain',
  'employee.findings.none': 'No findings for this period.',
  'employee.findings.noneOfTone': 'None.',
  'employee.findings.seeEvidence': 'See the evidence',
  'employee.flags.title': 'Context',
  'employee.flags.subtitle': 'Read the score with these in mind.',
  'employee.flags.none': 'Nothing to note.',
  'employee.trend.title': 'Trend',
  'employee.trend.byDay': 'The score of each day, from that day’s own evidence',
  'employee.trend.byMonth': 'The score of each month, from that month’s own evidence',
  'employee.trend.score': 'Score',
  'employee.trend.month': 'Month',
  'employee.trend.gaps': 'A gap is a day (or a month) without enough evidence for a score — not a zero.',
  'employee.trend.summary': 'A score on {scored} of {total}; the lowest {min}, the highest {max}.',
  'employee.trend.allNa': 'Not one day (or month) of this period had enough evidence for a score.',
  'employee.trend.emptyHint': 'Nothing to draw for this period.',
  'employee.trend.table': 'Show the figures as a table',
  'employee.revisions.title': 'Stored evaluations',
  'employee.revisions.subtitle': 'A closed day or month is stored with its report; a change in its sources makes a new revision, never an overwrite.',
  'employee.revisions.revision': 'Revision',
  'employee.revisions.current': 'Current',
  'employee.revisions.why': 'Why',
  'employee.revisions.first': 'First evaluation',
  'employee.revisions.reason.SOURCES_CHANGED': 'Its sources changed',
  'employee.revisions.computed': 'Computed',
  'employee.revisions.finalized': 'Settled',
  'employee.revisions.notStoredKind': 'Only days and months are stored as evaluations: pick a day or a month to see them.',
  'employee.revisions.live': 'This period is still running: it is stored once it closes.',
  'employee.revisions.none': 'Nothing stored for this period yet — it is stored with the period’s report.',
  'employee.ai.title': 'AI explanation',
  'employee.ai.ask': 'Ask AI to explain',
  'employee.ai.askAgain': 'Ask AI again',
  'employee.ai.requested': 'AI explanation requested.',
  'employee.ai.closedOnly': 'AI explains a closed day or month. Pick a past day or month to ask.',
  'employee.ai.notStored': 'This period has no stored evaluation yet — AI can explain it once the period’s report has stored it.',
  'employee.ai.neverScore': 'AI only explains the stored figures: it never sets or changes a score.',

  /* ---------------------------------------------------------------- Dimensions ------------- */
  'dimension.completion': 'Completion',
  'dimension.deadline': 'Deadlines',
  'dimension.quality': 'Quality',
  'dimension.tickets': 'Tickets',
  'dimension.collaboration': 'Collaboration',
  'dimension.completion.formula':
    'Items finished (tasks delivered, workflow steps, standalone Q Tasks, tickets resolved) ÷ (items finished + their items that fell due in the period and are still open).',
  'dimension.deadline.formula': 'Finished on or before the deadline ÷ (finished with a deadline + still open past it).',
  'dimension.quality.formula':
    'Their delivered work approved by someone else ÷ (approved + sent back for rework). A signal, never proof of quality: work nobody reviewed is left out, not failed.',
  'dimension.tickets.formula':
    '(First views within a view SLA they were held to + resolutions that stayed resolved) ÷ (those views decided + resolutions). The assignment SLA is the department’s, not theirs.',
  'dimension.collaboration.formula':
    '(Reviews of others’ work done within the threshold + workflow steps handed on in time) ÷ (reviews done + reviews still waiting past the threshold + workflow steps with a deadline).',

  /* ---------------------------------------------------------------- Findings --------------- */
  'finding.DEADLINES_KEPT': '{onTime} of {total} items with a deadline were finished on time.',
  'finding.ITEMS_PAST_DEADLINE': 'Items that fell due in the period and are still open: {count}.',
  'finding.FINISHED_LATE': 'Items finished after their deadline: {count}.',
  'finding.WORK_APPROVED': '{approved} of {total} reviewed items were approved.',
  'finding.WORK_SENT_BACK': 'Items sent back for rework: {count} — a signal to look at, not proof.',
  'finding.TICKETS_REOPENED': 'Resolved tickets that were reopened: {count}.',
  'finding.VIEW_SLA_LATE': 'Tickets not viewed within their view SLA: {count}.',
  'finding.STEADY_DELIVERY': 'Items finished: {count}, none of them past its deadline.',
  'finding.REVIEWED_OTHERS': 'Items of other people’s work they reviewed: {count}.',
  'finding.REVIEWS_WAITING': 'Reviews that waited on them past the threshold: {count}.',
  'finding.BLOCKED_WORK': 'Items of theirs that are blocked: {count} — context, not counted against them.',
  'finding.AWAITING_REVIEW': 'Delivered items still awaiting review: {count}.',
  'finding.NEW_EMPLOYEE': 'Joined within the last 30 days of the period — read the score with that in mind.',
  'finding.OVERDUE_BACKLOG': 'Items already overdue before the period: {count} — context, not scored again.',
  'finding.DIMENSION_NOT_MEASURABLE': '{dimension} could not be measured: there was no evidence to divide.',

  /* ---------------------------------------------------------------- Flags ------------------ */
  'flag.NEW_EMPLOYEE': 'New employee',
  'flag.NEW_EMPLOYEE.hint': 'Joined within 30 days of the period’s end — never ranked low for it.',
  'flag.BLOCKED_WORK': 'Blocked work',
  'flag.BLOCKED_WORK.hint': 'Some of their work is blocked; it is context, and never scored.',
  'flag.OVERDUE_BACKLOG': 'Overdue backlog',
  'flag.OVERDUE_BACKLOG.hint': 'Items overdue from before the period; not scored again here.',
  'flag.AWAITING_REVIEW': 'Awaiting review',
  'flag.AWAITING_REVIEW.hint': 'Delivered work nobody has reviewed yet.',
  'flag.SELF_COMPLETED_UNREVIEWED': 'Done without review',
  'flag.SELF_COMPLETED_UNREVIEWED.hint': 'Work they marked done themselves, with no reviewer to approve it.',
  'flag.SMALL_SAMPLE': 'Small sample',
  'flag.SMALL_SAMPLE.hint': 'A dimension rests on fewer than three items.',
  'flag.NO_DEADLINE_EVIDENCE': 'No deadline evidence',
  'flag.NO_DEADLINE_EVIDENCE.hint': 'None of their work had a deadline in the period, so deadlines could not be measured.',

  /* ---------------------------------------------------------------- Evidence --------------- */
  'evidence.listNote':
    'Every record behind the score, the newest first: what it was, when, and how it went. The titles are shown to you; the AI explanation never sees them.',
  'evidence.empty': 'No evidence in this period',
  'evidence.emptyHint': 'Nothing of theirs was finished, fell due or was reviewed in this period.',
  'evidence.col.when': 'When',
  'evidence.col.kind': 'Evidence',
  'evidence.col.record': 'Record',
  'evidence.col.deadline': 'Deadline',
  'evidence.col.outcome': 'Outcome',
  'evidence.col.verification': 'Verification',
  'evidence.recordGone': 'The record is no longer available',
  'evidence.kind.TASK_DELIVERED': 'Task delivered',
  'evidence.kind.WORKFLOW_STEP': 'Workflow step finished',
  'evidence.kind.QTASK_DONE': 'Q Task done',
  'evidence.kind.TICKET_RESOLVED': 'Ticket resolved',
  'evidence.kind.DUE_MISSED': 'Deadline missed',
  'evidence.kind.REVIEW_APPROVED': 'Their work approved',
  'evidence.kind.REVIEW_RETURNED': 'Their work sent back',
  'evidence.kind.VIEW_SLA': 'Ticket view SLA',
  'evidence.kind.REVIEW_DONE': 'Reviewed someone’s work',
  'evidence.kind.REVIEW_OVERDUE': 'A review waiting on them',
  'evidence.kind.BLOCKED_CONTEXT': 'Blocked (context)',
  'evidence.source.TASK': 'Task',
  'evidence.source.Q_TASK': 'Q Task',
  'evidence.source.TICKET': 'Ticket',
  'evidence.verification.APPROVED': 'Approved by a reviewer',
  'evidence.verification.WORKFLOW_STEP': 'Handed on in its workflow',
  'evidence.verification.AWAITING_REVIEW': 'Awaiting review',
  'evidence.verification.SELF_DONE': 'Done with no review',
  'evidence.outcome.onTime': 'On time',
  'evidence.outcome.late': 'Late',
  'evidence.outcome.noDeadline': 'No deadline',
  'evidence.outcome.stayed': 'Stayed resolved',
  'evidence.outcome.reopened': 'Reopened',
  'evidence.outcome.missed': 'Missed',
  'evidence.outcome.blocked': 'Blocked — not scored',
  'evidence.outcome.approved': 'Approved',
  'evidence.outcome.returned': 'Sent back',
  'evidence.outcome.reviewWaiting': 'Waiting past the threshold',
  'evidence.outcome.reviewInTime': 'Reviewed in time',
  'evidence.outcome.reviewLate': 'Reviewed late',
  'evidence.outcome.viewedInTime': 'Viewed in time',
  'evidence.outcome.viewedLate': 'Not viewed in time',
  'evidence.back': 'Back',
  'evidence.badType': 'A record here is a task, a ticket or a Q Task.',
  'evidence.item.summary': 'Summary',
  'evidence.item.dates': 'Dates',
  'evidence.item.assignee': 'Assignee',
  'evidence.item.createdBy': 'Created by',
  'evidence.item.wholeDepartment': 'The whole department',
  'evidence.item.workflow': 'Workflow',
  'evidence.item.noWorkflow': 'Not in a workflow',
  'evidence.item.fromQTask': 'From Q Task',
  'evidence.item.kindLabel': 'Kind',
  'evidence.item.leaderTask': 'Leaders Task',
  'evidence.item.targetDepartment': 'Department',
  'evidence.item.converted': 'Converted into tasks',
  'evidence.item.convertedYes': 'Yes — its tasks carry the work',
  'evidence.item.created': 'Created',
  'evidence.item.started': 'Started',
  'evidence.item.completed': 'Completed',
  'evidence.item.assigned': 'Assigned',
  'evidence.item.resolved': 'Resolved',
  'evidence.item.closed': 'Closed',
  'evidence.item.viewDeadline': 'View deadline',
  'evidence.item.assignmentDeadline': 'Assignment deadline',
  'evidence.item.views': 'First views',
  'evidence.item.viewsHint': 'Who opened the ticket first, and whether they were held to its view SLA.',
  'evidence.item.noViews': 'Nobody has viewed it yet.',
  'evidence.item.firstViewed': 'First viewed',
  'evidence.item.eligible': 'Held to the view SLA',
  'evidence.item.notJudged': 'Not judged',
  'evidence.item.becameTasks': 'The tasks it became',
  'evidence.item.becameTasksHint': 'A converted Q Task counts through these tasks.',
  'evidence.item.taskN': 'Task {n}',
  'evidence.item.timeline': 'Timeline',
  'evidence.item.timelineHint': 'Its moves, who made them and when.',
  'evidence.item.noTimeline': 'No moves recorded.',
  'evidence.item.system': 'The system',
  'evidence.item.noContent': 'Read-only: its description, comments and files are not shown on the dashboard.',
  'evidence.action.task_status_changed': 'Status changed',
  'evidence.action.task_assigned': 'Assigned',
  'evidence.action.task_reassigned': 'Reassigned',
  'evidence.action.task_deadline_set': 'Deadline set',
  'evidence.action.task_returned': 'Sent back',
  'evidence.action.task_workflow_handed_on': 'Handed on to the next level',
  'evidence.action.ticket_status_changed': 'Status changed',
  'evidence.action.ticket_assigned': 'Assigned',
  'evidence.action.ticket_reassigned': 'Reassigned',
  'evidence.action.ticket_resolved': 'Resolved',
  'evidence.action.ticket_reopened': 'Reopened',
  'evidence.action.ticket_claim': 'Claimed',
  'evidence.action.ticket_rerouted': 'Moved to another department',
  'evidence.action.q_task_created': 'Created',
  'evidence.action.q_task_available': 'Made available',
  'evidence.action.q_task_claimed': 'Claimed',
  'evidence.action.q_task_assigned': 'Assigned',
  'evidence.action.q_task_reassigned': 'Reassigned',
  'evidence.action.q_task_released': 'Released',
  'evidence.action.q_task_force_released': 'Released by a leader',
  'evidence.action.q_task_started': 'Started',
  'evidence.action.q_task_blocked': 'Blocked',
  'evidence.action.q_task_unblocked': 'Unblocked',
  'evidence.action.q_task_rescheduled': 'Deadline moved',
  'evidence.action.q_task_completed': 'Completed',
  'evidence.action.q_task_skipped': 'Skipped',
  'evidence.action.q_task_cancelled': 'Cancelled',
  'evidence.action.q_task_converted': 'Converted into tasks',
  'evidence.action.q_task_done_by_tasks': 'Done — its tasks finished',
  'evidence.action.q_task_reopened_by_task': 'Reopened by one of its tasks',
  'evidence.action.q_task_cancelled_by_tasks': 'Cancelled — its tasks were cancelled',

  /* ---------------------------------------------------------------- Points & Rewards ------- */
  'points.title': 'Points & Rewards',
  'points.subtitle': 'The wallet’s own ledger: points granted, redeemed and refunded, rewards and cash — kept apart from work performance.',
  'points.allEmployees': 'All employees',
  'points.employeeWins': 'An employee is picked: the department filter is set aside.',
  'points.separate': 'Points are not the performance score: no score becomes points here, and no point counts toward a score.',
  'points.private': 'Why points were granted, and who granted them, stay private to the administrators — they are never sent to this dashboard.',
  'points.daysOnly': 'Day rewards are shown in days, never as money: a day reward is a day of someone’s pay.',
  'points.totals.granted': 'Granted',
  'points.totals.grantedHint': 'Points credited in the period',
  'points.totals.redeemed': 'Redeemed',
  'points.totals.redeemedHint': 'Points spent on rewards',
  'points.totals.refunded': 'Refunded',
  'points.totals.refundedHint': 'Points returned from cancelled redemptions',
  'points.totals.net': 'Net',
  'points.totals.netHint': 'Granted − redeemed + refunded',
  'points.bySource.title': 'Granted, by source',
  'points.bySource.subtitle': 'Where the period’s credits came from.',
  'points.source.AI_DAILY_POINTS': 'AI Daily Points',
  'points.source.ADMINISTRATOR': 'An administrator',
  'points.source.OTHER': 'Other',
  'points.balances.title': 'Balances now',
  'points.balances.subtitle': 'Today’s balances, whatever the period.',
  'points.balances.total': 'Points held',
  'points.balances.wallets': 'Wallets',
  'points.balances.withPoints': 'Wallets with points',
  'points.trend.title': 'Points over time',
  'points.trend.byDay': 'By day',
  'points.trend.byMonth': 'By month',
  'points.trend.empty': 'No points moved in this period',
  'points.trend.summary': 'In the period: {granted} granted, {redeemed} redeemed, {refunded} refunded.',
  'points.trend.table': 'Show the figures as a table',
  'points.redemptions.byType': 'Redemptions by type and status',
  'points.redemptions.byTypeHint': 'How many were redeemed in the period, and the points they cost.',
  'points.redemptions.title': 'Redemptions',
  'points.redemptions.subtitle': 'Each on its own date; a cash reward’s payment has its own date too.',
  'points.redemptions.empty': 'No redemptions in this period',
  'points.redemptions.type': 'Reward',
  'points.redemptions.redeemedAt': 'Redeemed',
  'points.redemptions.completedAt': 'Completed',
  'points.redemptions.cash': 'Cash',
  'points.redemptions.cancelledOn': 'Cancelled {date}',
  'points.redemption.REMOVE_QUARTER_DAY_PENALTY': 'Cancel a quarter-day deduction',
  'points.redemption.REMOVE_HALF_DAY_PENALTY': 'Cancel a half-day deduction',
  'points.redemption.REMOVE_FULL_DAY_PENALTY': 'Cancel a full-day deduction',
  'points.redemption.ADD_ONE_LEAVE_DAY': 'Add a day of leave',
  'points.redemption.CASH_REWARD': 'Points for cash',
  'points.redemptionStatus.COMPLETED': 'Done',
  'points.redemptionStatus.PENDING_SETTLEMENT': 'Cash pending',
  'points.redemptionStatus.CANCELLED': 'Cancelled',
  'points.cashStatus.PENDING': 'Not paid yet',
  'points.cashStatus.PAID': 'Paid',
  'points.cashStatus.CANCELLED': 'Cancelled',
  'points.dayRewardStatus.GRANTED': 'To be paid',
  'points.dayRewardStatus.PAID': 'Paid',
  'points.dayRewardStatus.CANCELLED': 'Withdrawn',
  'points.kind.CREDIT': 'Points added',
  'points.kind.REDEMPTION_DEBIT': 'Redeemed',
  'points.kind.REFUND_CREDIT': 'Returned from a redemption',
  'points.pointsValue': 'Points: {n}',
  'points.cash.title': 'Cash rewards, by currency',
  'points.cash.subtitle': 'Each amount in its own currency.',
  'points.cash.empty': 'No cash rewards in this period',
  'points.cash.requested': 'Requested',
  'points.cash.paid': 'Paid',
  'points.cash.cancelled': 'Cancelled',
  'points.cash.pending': 'Still pending',
  'points.cash.note':
    'Requested counts on the day it was asked for; paid only once it was paid, on the day it was paid; still pending is what was asked for in the period and is not paid yet.',
  'points.cash.paidOn': 'Paid {date}',
  'points.cash.notPaid': 'Not paid yet',
  'points.dayRewards.title': 'Day rewards',
  'points.dayRewards.subtitle': 'In days — never the pay they are worth.',
  'points.dayRewards.summarySubtitle': 'Granted in the period, in days — never the pay they are worth.',
  'points.dayRewards.empty': 'No day rewards in this period',
  'points.dayRewards.days': 'Days',
  'points.dayRewards.month': 'Payroll month',
  'points.dayRewards.grantedAt': 'Granted',
  'points.dayRewards.settled': 'Paid or withdrawn',
  'points.dayRewards.rewards': 'Rewards',
  'points.dayRewards.totalDays': 'Days in all',
  'points.dayRewards.parts': 'Made of',
  'points.dayRewards.part': '{n} × {days} day',
  'points.employees.title': 'Employees',
  'points.employees.subtitle': 'The points each person was granted, redeemed and refunded in the period, and their balance now.',
  'points.employees.empty': 'No wallet moved in this period',
  'points.employees.balance': 'Balance now',
  'points.balance.current': 'Balance now',
  'points.balance.currentHint': 'Today, whatever the period',
  'points.balance.opening': 'At the period’s start',
  'points.balance.openingHint': 'Before its first entry',
  'points.balance.closing': 'At the period’s end',
  'points.balance.closingHint': 'After its last entry',
  'points.ledger.title': 'Ledger',
  'points.ledger.subtitle': 'Every entry in the period, the oldest first — without its reason.',
  'points.ledger.empty': 'No entries in this period',
  'points.ledger.entry': 'Entry',
  'points.ledger.source': 'Source',
  'points.ledger.points': 'Points',
  'points.ledger.balanceAfter': 'Balance after',

  /* ---------------------------------------------------------------- Work & Productivity ---- */
  'work.title': 'Work & Productivity',
  'work.subtitle': 'Tasks, tickets, Q Tasks and workflows — each work item counted once.',
  'work.allProjects': 'All projects',
  'work.tab.summary': 'Summary',
  'work.tab.departments': 'Departments',
  'work.tab.workload': 'Workload',
  'work.na': 'N/A',
  'work.hours': '{n} h',
  'work.days': '{n} d',
  'work.now': 'Now',
  'work.nowHint': 'Now',
  'work.inPeriod': 'In the period',
  'work.inPeriodHint': 'In the period',
  'work.countedOnce':
    'Each work item once: a workflow is one root item when its run completes, and its tasks are its steps; a Q Task converted into tasks counts through those tasks; a task a whole department shares is one task. Leaders Tasks are counted apart.',
  'work.firstOf': 'The first {shown} of {total}.',
  'work.projectFilterNote': 'Q Tasks, violations and Leaders Tasks belong to no project: with a project picked, their figures are still the department’s (or the company’s).',
  'work.daysBlocked': 'Days blocked: {n}',
  'work.tasks.title': 'Tasks',
  'work.tasks.subtitle': 'Leaders Tasks are left out here and counted below.',
  'work.tasks.uniqueRoot': 'Unique root items completed',
  'work.tasks.uniqueRootHint': 'Tasks outside a workflow, and workflows completed — each once',
  'work.tasks.steps': 'Workflow steps completed',
  'work.tasks.stepsHint': 'Steps, not separate root items',
  'work.tasks.completed': 'Tasks done outside workflows',
  'work.tasks.fromQTasks': 'Of them from Q Tasks: {n}',
  'work.tasks.created': 'Created',
  'work.tasks.cancelled': 'Cancelled',
  'work.tasks.newlyOverdue': 'Fell overdue',
  'work.tasks.newlyOverdueHint': 'The deadline passed in the period; still open',
  'work.tasks.open.toDo': 'To do',
  'work.tasks.open.inProgress': 'In progress',
  'work.tasks.open.blocked': 'Blocked',
  'work.tasks.open.review': 'In review',
  'work.tasks.overdue': 'Overdue',
  'work.tasks.overdueHint': 'Open past the deadline (not in review)',
  'work.tasks.dueSoon': 'Due within 48 hours',
  'work.tasks.dueSoonHint': 'Open, not in review',
  'work.tasks.cycle.title': 'Cycle time — elapsed time, not working time',
  'work.tasks.cycle.median': 'Median',
  'work.tasks.cycle.p90': '90th percentile',
  'work.tasks.cycle.sample': 'Tasks measured',
  'work.tasks.cycle.note': 'From creation to done, on the clock — nights, weekends and waiting included. The system keeps no record of the time actually worked.',
  'work.tickets.title': 'Tickets',
  'work.tickets.subtitle': 'A ticket resolved twice is one ticket and two resolutions.',
  'work.tickets.created': 'Created',
  'work.tickets.resolvedTickets': 'Tickets resolved',
  'work.tickets.resolvedTicketsHint': 'Each ticket once',
  'work.tickets.resolutionEvents': 'Resolutions',
  'work.tickets.resolutionEventsHint': 'Every time a ticket was resolved',
  'work.tickets.reopenEvents': 'Reopenings',
  'work.tickets.reopenRate': 'Reopen rate',
  'work.tickets.reopenRateHint': 'Reopenings ÷ resolutions',
  'work.tickets.closed': 'Closed',
  'work.tickets.viewSla': 'View SLA met',
  'work.tickets.assignmentSla': 'Assignment SLA met',
  'work.tickets.slaHint': '{onTime} of {decided} decided',
  'work.tickets.slaNone': 'None decided yet',
  'work.tickets.open.new': 'New',
  'work.tickets.open.assigned': 'Assigned',
  'work.tickets.open.inProgress': 'In progress',
  'work.tickets.open.waiting': 'Waiting for the requester',
  'work.tickets.open.reopened': 'Reopened',
  'work.tickets.urgentOpen': 'Urgent, open',
  'work.tickets.highOpen': 'High, open',
  'work.tickets.urgentHighUnassigned': 'Urgent or high, unassigned',
  'work.tickets.unassignedPastDeadline': 'Unassigned past the assignment deadline',
  'work.tickets.openViolations': 'Open violations',
  'work.tickets.openViolationsHint': 'Raised in the period, still open',
  'work.qTasks.title': 'Q Tasks',
  'work.qTasks.subtitle': 'A converted Q Task counts through its tasks; a standalone one done counts once.',
  'work.qTasks.available': 'Available',
  'work.qTasks.claimed': 'Claimed',
  'work.qTasks.inProgress': 'In progress',
  'work.qTasks.blocked': 'Blocked',
  'work.qTasks.queueAge': 'Median wait',
  'work.qTasks.queueAgeHint': 'Of the ones available now',
  'work.qTasks.doneStandalone': 'Done (standalone)',
  'work.qTasks.doneStandaloneHint': 'Not converted into tasks',
  'work.qTasks.converted': 'Converted into tasks',
  'work.qTasks.convertedHint': 'Counted through their tasks',
  'work.qTasks.skipped': 'Skipped',
  'work.qTasks.longBlocked': 'Blocked longer than the settings allow: {n}',
  'work.qTasks.longBlockedNone': 'None.',
  'work.workflows.title': 'Workflows',
  'work.workflows.subtitle': 'A workflow is one root item; the tasks in it are its steps.',
  'work.workflows.active': 'Active',
  'work.workflows.completed': 'Completed',
  'work.workflows.stopped': 'Stopped',
  'work.workflows.steps': 'Steps completed',
  'work.workflows.stalled': 'Stalled — no move for longer than the settings allow: {n}',
  'work.workflows.stalledNone': 'None.',
  'work.workflows.level': 'Level {n}',
  'work.workflows.hasBlocked': 'A step is blocked',
  'work.workflows.idle': 'Days without a move: {n}',
  'work.leaders.title': 'Leaders Tasks',
  'work.leaders.subtitle': 'Counted apart: the work they hand on is what the task figures count.',
  'work.leaders.open': 'Open',
  'work.leaders.done': 'Done',
  'work.departments.note': 'Each department against its own previous period — not a ranking: departments do different work.',
  'work.departments.empty': 'No departments to show',
  'work.departments.chartTitle': 'Backlog and completed work',
  'work.departments.chartSubtitle': 'Open items now; items completed in the period and in the one before.',
  'work.departments.chartLabel': 'The backlog, and the work completed in this period and the one before, for {n} departments. The table below holds every figure.',
  'work.departments.chartTwin': 'Every figure in the chart is in the table below.',
  'work.departments.backlog': 'Backlog now',
  'work.departments.completed': 'Completed',
  'work.departments.previous': 'Completed, the period before',
  'work.departments.members': 'Members',
  'work.departments.openTasks': 'Open tasks',
  'work.departments.openTickets': 'Open tickets',
  'work.departments.openQTasks': 'Open Q Tasks',
  'work.departments.overdue': 'Overdue tasks',
  'work.departments.doneTasks': 'Tasks done',
  'work.departments.doneTickets': 'Tickets resolved',
  'work.departments.doneQTasks': 'Q Tasks done',
  'work.departments.change': 'Change',
  'work.workload.note':
    'The open work each person holds now, with its deadlines — not “utilization”: the system keeps no time estimates, so it cannot say how busy anyone is.',
  'work.workload.empty': 'Nobody holds open work',
  'work.workload.openTasks': 'Open tasks',
  'work.workload.overdue': 'Overdue',
  'work.workload.dueSoon': 'Due within 48 hours',
  'work.workload.openTickets': 'Open tickets',
  'work.workload.openQTasks': 'Open Q Tasks',
  'work.workload.total': 'Open items',
  'work.workload.limit': 'Shows the 100 people holding the most open work.',

  /* ---------------------------------------------------------------- Projects --------------- */
  'projects.title': 'Projects & Clients',
  'projects.subtitle': 'Delivery health from each project’s own tasks and dates; service health from SLAs and reopened tickets.',
  'projects.tab.projects': 'Projects',
  'projects.tab.clients': 'Clients',
  'projects.searchPlaceholder': 'Search by name or code',
  'projects.allStatuses': 'All statuses',
  'projects.allRisks': 'All tiers',
  'projects.allClients': 'All clients',
  'projects.count.total': 'Projects',
  'projects.count.delivering': 'Delivering',
  'projects.count.deliveringHint': 'Draft, active, testing or on hold',
  'projects.count.support': 'In support',
  'projects.count.atRisk': 'At risk',
  'projects.count.atRiskHint': 'High delivery risk',
  'projects.count.watch': 'Watch',
  'projects.count.watchHint': 'Medium delivery risk',
  'projects.countsBeforeRisk': 'The counts above are before the tier filter.',
  'projects.empty': 'No projects match',
  'projects.col.risk': 'Delivery risk',
  'projects.col.progress': 'Progress',
  'projects.col.overdueBlocked': 'Overdue · blocked',
  'projects.col.openTickets': 'Open tickets',
  'projects.col.support': 'Support coverage',
  'projects.col.invoiced': 'Invoiced to date',
  'projects.col.collected': 'Collected to date',
  'projects.col.outstanding': 'Outstanding now',
  'projects.risk.HIGH': 'At risk',
  'projects.risk.MEDIUM': 'Watch',
  'projects.risk.LOW': 'On track',
  'projects.risk.NONE': 'Not assessed',
  'projects.reason.PAST_EXPECTED_END': 'Past its expected end, with work still open',
  'projects.reason.OVERDUE_TASKS': 'Three or more tasks overdue',
  'projects.reason.OVERDUE_SHARE': 'Much of its dated work overdue',
  'projects.reason.OVERDUE_TASK': 'A task overdue',
  'projects.reason.BLOCKED_TASKS': 'Two or more tasks blocked',
  'projects.reason.ON_HOLD': 'On hold',
  'projects.reason.NO_TRACKED_TASKS': 'No tracked tasks and no end date',
  'projects.progress': '{done} of {tracked} tracked tasks complete',
  'projects.noTrackedTasks': 'No tracked tasks',
  'projects.noInvoices': 'No invoices',
  'projects.noRate': 'No rate for {currencies} — left out, never counted as zero.',
  'projects.supportUntil': 'Until {date}',
  'projects.noSupport': 'No contract covers it today',
  'projects.progressNote': 'Progress is a count of tracked tasks complete — never a percentage of the project, which has no scope or milestones to measure.',
  'projects.moneyNote': 'Finance’s receivable invoices that name the project, to date — not limited to the period.',
  'projects.marginNote': 'Margin is not available: no costs are attributed to projects.',
  'projects.back': 'Projects & Clients',
  'projects.beyondList': 'This project is beyond what the dashboard reads at once.',
  'projects.owner': 'Owner',
  'projects.detail.overdueTasks': 'Overdue tasks',
  'projects.detail.openBlocked': 'Open: {open} · in review: {review} · blocked: {blocked}',
  'projects.detail.resolvedInPeriod': 'Resolved in the period: {n}',
  'projects.detail.money': 'Money',
  'projects.detail.margin': 'Margin',
  'projects.detail.dates': 'Dates',
  'projects.detail.start': 'Start',
  'projects.detail.expectedEnd': 'Expected end',
  'projects.detail.actualEnd': 'Actual end',
  'projects.detail.end': 'End',
  'projects.detail.contract': 'Contract',
  'projects.detail.until': 'Covers until',
  'projects.detail.contracts': 'Contracts',
  'projects.detail.noContracts': 'No contract names this project.',
  'projects.detail.billing': 'Billing',
  'projects.detail.overdueList': 'Overdue tasks',
  'projects.detail.overdueListHint': 'Open past their deadline, the oldest first.',
  'projects.detail.noOverdue': 'No task is overdue.',
  'projects.detail.blockedList': 'Blocked tasks',
  'projects.detail.blockedListHint': 'The longest unchanged first.',
  'projects.detail.noBlocked': 'No task is blocked.',
  'projects.detail.ticketsList': 'Open tickets',
  'projects.detail.ticketsListHint': 'The most urgent first.',
  'projects.detail.noTickets': 'No open tickets.',
  'projects.detail.task': 'Task',
  'projects.detail.ticket': 'Ticket',
  'projects.detail.opened': 'Opened',
  'projects.detail.lastChange': 'Last change',
  'projects.detail.capped': 'The first {n} are shown.',
  'projects.detail.weeklyTitle': 'Tracked tasks completed, by week',
  'projects.detail.weeklySubtitle': 'The last 12 weeks, each from the company’s first day of the week — whatever the period.',
  'projects.detail.weeklySeries': 'Tasks completed',
  'projects.detail.weeklyNone': 'No tracked task was completed in the last 12 weeks.',
  'projects.detail.weeklyLabel': 'Tracked tasks completed: {n}, over {weeks} weeks.',
  'projects.detail.weeklyTable': 'Show the figures as a table',
  'projects.detail.week': 'Week',
  'projects.detail.weekOf': 'Week of {date}',

  /* ---------------------------------------------------------------- Clients ---------------- */
  'clients.searchPlaceholder': 'Search by name or code',
  'clients.serviceNote': 'Service health is SLA compliance and reopened tickets — there is no satisfaction survey, so there is no satisfaction score.',
  'clients.tableTitle': 'Clients',
  'clients.tableSubtitle': 'Tickets and collections in the period; what they owe, now.',
  'clients.empty': 'No clients match',
  'clients.col.projects': 'Projects',
  'clients.col.openTickets': 'Open tickets',
  'clients.col.resolved': 'Resolved in the period',
  'clients.col.reopened': 'Reopened in the period',
  'clients.col.assignmentSla': 'Assignment SLA met',
  'clients.col.viewSla': 'View SLA met',
  'clients.col.collected': 'Collected in the period',
  'clients.col.outstanding': 'Outstanding now',
  'clients.col.overdueInvoices': 'Overdue invoices',
  'clients.col.contracts': 'Contracts',
  'clients.delivering': 'Delivering: {n}',
  'clients.noContract': 'No contract in force',
  'clients.covering': 'In force: {n}',
  'clients.endingSoon': 'Ending within {days} days: {n}',
  'clients.slaNote': 'SLA met: of the tickets opened in the period whose deadline has been decided, the share handled in time. N/A where none was decided.',
  'clients.moneyNote': 'Collected: payments received in the period, in the base currency. Outstanding: what their invoices still owe today.',
  'clients.back': 'Clients',
  'clients.beyondList': 'This client is beyond what the dashboard reads at once.',
  'clients.service.title': 'Service health',
  'clients.service.subtitle': 'The SLA compliance of tickets opened in the period.',
  'clients.service.decided': 'Tickets decided',
  'clients.service.satisfaction': 'Satisfaction',
  'clients.service.noSurvey': 'There is no satisfaction survey, so no satisfaction score is shown.',
  'clients.money.title': 'Money',
  'clients.contracts.covering': 'In force today',
  'clients.contracts.endingSoon': 'Ending within {days} days',
  'clients.detail.projects': 'Projects',
  'clients.detail.noProjects': 'No projects for this client.',
  'clients.detail.tickets': 'Recent tickets',
  'clients.detail.ticketsHint': 'The 25 newest, whatever the period.',
  'clients.detail.noTickets': 'No tickets.',
  'clients.detail.invoices': 'Open invoices',
  'clients.detail.invoicesHint': 'Issued and not fully paid, the earliest due first — each in its own currency.',
  'clients.detail.noInvoices': 'No open invoices.',
  'clients.detail.invoice': 'Invoice',
  'clients.detail.issued': 'Issued',
  'clients.detail.due': 'Due',
  'clients.detail.total': 'Total',
  'clients.detail.remaining': 'Remaining',
  'clients.detail.overdue': 'Overdue',

  /* ---------------------------------------------------------------- Dashboard Access ------- */
  'access.title': 'Dashboard Access',
  'access.subtitle': 'Who may open the Executive Dashboard.',
  'access.readOnly': 'Read-only: access is granted and revoked in the admin — Settings › Executive Dashboard.',
  'access.who': 'Only an active Administrator who holds Dashboard Access can sign in, and both are checked again on every request.',
  'access.empty': 'Nobody holds Dashboard Access',
  'access.emptyHint': 'It is granted in the admin: Settings › Executive Dashboard.',
  'access.col.name': 'Name',
  'access.col.jobTitle': 'Job title',
  'access.col.grantedAt': 'Granted',
  'access.col.grantedBy': 'Granted by',
  'access.col.lastSignIn': 'Last sign-in',
  'access.col.state': 'State',
  'access.grantedBySystem': 'Not recorded',
  'access.never': 'Never',
  'access.honoured': 'Active',
  'access.notHonoured': 'Not honoured',
  'access.notHonouredHint': 'Still recorded, but they are no longer an active Administrator: their next request ends their session.',

  /* ---------------------------------------------------------------- Dashboard Settings ----- */
  'settings.title': 'Dashboard Settings',
  'settings.subtitle': 'The settings in force today, read-only.',
  'settings.defaults': 'Defaults — nothing saved yet',
  'settings.version': 'Version {n}',
  'settings.inForceSince': 'In force since {date}',
  'settings.readOnly': 'Read-only: they are changed in the admin — Settings › Executive Dashboard.',
  'settings.versions':
    'Each save is a new version, in force from its day on; nothing already finalized is scored again. The weights are starting defaults the company owns, not a validated standard.',
  'settings.on': 'On',
  'settings.off': 'Off',
  'settings.scoring.title': 'Work score',
  'settings.scoring.subtitle': 'Its dimensions, their weights, and when a number is shown.',
  'settings.scoring.state': 'State',
  'settings.scoring.minEvents': 'Work events needed before a score',
  'settings.scoring.minWeight': 'Share of the weight that must be measurable',
  'settings.scoring.reviewHours': 'A review counts as late after (hours)',
  'settings.labels.title': 'Labels',
  'settings.labels.subtitle': 'The word a score is given — the number always shows too.',
  'settings.labels.atLeast': '{n} and above',
  'settings.labels.below': 'Below {n}',
  'settings.labels.note': 'A label never replaces the number, and no score is compared across departments.',
  'settings.overrides.title': 'Department weights',
  'settings.overrides.subtitle': 'A department’s own weights, used for the people whose primary department it is.',
  'settings.overrides.none': 'No department has its own weights: everyone is scored with the weights above.',
  'settings.unknownDepartment': 'A department no longer active',
  'settings.health.title': 'Company health',
  'settings.health.subtitle': 'The pillars’ weights, and when each one can be shown.',
  'settings.health.financial': 'Financial',
  'settings.health.work': 'Work',
  'settings.health.delivery': 'Delivery',
  'settings.health.service': 'Service',
  'settings.health.minWeight': 'Share of the weight that must be measurable',
  'settings.health.minScored': 'People with a score, for the team pillar',
  'settings.health.minTickets': 'Tickets, for the service pillar',
  'settings.health.minDatedTasks': 'Open dated tasks, for the delivery pillar',
  'settings.risks.title': 'Risk thresholds',
  'settings.risks.subtitle': 'When the dashboard raises a risk.',
  'settings.risks.blockedQTaskDays': 'A Q Task is long-blocked after (days)',
  'settings.risks.stalledWorkflowDays': 'A workflow is stalled after (days)',
  'settings.risks.contractExpiryDays': 'A contract is ending soon within (days)',
  'settings.risks.overdueReceivableShare': 'The overdue share of receivables',
  'settings.risks.expenseSpike': 'An expense spike',
  'settings.risks.receivableConcentration': 'Receivables owed by one client',
  'settings.schedule.title': 'Report schedule',
  'settings.schedule.daily': 'The daily report, after the day closes',
  'settings.schedule.monthly': 'The monthly report, on the 1st',
  'settings.schedule.weekly': 'The weekly report',
  'settings.schedule.settleDays': 'Days before a report is settled',
  'settings.schedule.note': 'A report is provisional until it settles; its sources are looked at again then, and a change makes a new revision.',
  'settings.retention.title': 'Retention',
  'settings.retention.snapshots': 'Report snapshots and evaluations (months)',
  'settings.retention.exports': 'The export log (months)',
  'settings.retention.forever': 'Kept for ever',
  'settings.ai.title': 'AI explanations',
  'settings.ai.subtitle': 'Optional: every figure stands without them.',
  'settings.ai.switch': 'The administrators’ switch',
  'settings.ai.availability': 'Available now',
  'settings.ai.state.ENABLED': 'Available',
  'settings.ai.state.DISABLED': 'Switched off',
  'settings.ai.state.NOT_CONFIGURED': 'Not set up on the server',
  'settings.ai.state.BUDGET_EXCEEDED': 'This month’s budget is spent',
  'settings.ai.model': 'The model in use',
  'settings.ai.noModel': 'None',
  'settings.ai.budget': 'Monthly budget (tokens)',
  'settings.ai.used': 'Used this month (tokens)',
  'settings.ai.notCounted': 'Not counted while AI is off',
  'settings.ai.reports.daily': 'Daily briefings',
  'settings.ai.reports.weekly': 'Weekly briefings',
  'settings.ai.reports.monthly': 'Monthly reports',
  'settings.ai.reports.employees': 'Employee explanations',
  'settings.ai.price': 'Price estimate, per million tokens',
  'settings.ai.priceValue': 'in {input} · out {output} {currency}',
  'settings.ai.noPrice': 'No price set — no cost estimate',
  'settings.ai.priceAsOf': 'Prices as of',
  'settings.ai.note': 'AI runs only when the server allows it and the administrators switch it on; it explains stored figures and never changes them.',
  'settings.ai.priceNote': 'Any cost shown is an estimate from these dated prices — never billing.',
} as const;

export const areaBAr: Record<keyof typeof areaBEn, string> = {
  /* ---------------------------------------------------------------- Employee Performance ---- */
  'employees.title': 'أداء الموظفين',
  'employees.subtitle': 'درجة عمل كل شخص خلال الفترة، من أدلة مهامه وخطوات سير العمل ومهام الطابور والتذاكر.',
  'employees.allDepartments': 'كل الأقسام',
  'employees.searchPlaceholder': 'ابحث بالاسم',
  'employees.sortLabel': 'الترتيب داخل كل قسم',
  'employees.sort.name': 'الاسم',
  'employees.sort.score': 'الدرجة، الأعلى أولًا',
  'employees.sort.evidence': 'الأدلة، الأكثر أولًا',
  'employees.kpi.people': 'الأشخاص',
  'employees.kpi.peopleHint': 'الموظفون النشطون، ومن لهم أدلة عمل خلال الفترة',
  'employees.kpi.scored': 'لهم درجة',
  'employees.kpi.scoredHint': 'أدلة كافية لاحتساب درجة',
  'employees.kpi.notEnough': 'لا توجد أدلة كافية',
  'employees.kpi.notEnoughHint': 'بلا درجة، ولا نحسبهم صفرًا أبدًا',
  'employees.kpi.coverage': 'التغطية',
  'employees.kpi.coverageHint': 'نسبة من لهم درجة',
  'employees.kpi.median': 'الدرجة الوسيطة',
  'employees.kpi.medianHint': 'لمن لهم درجة',
  'employees.kpi.medianNone': 'لم يحصل أحد على درجة بعد',
  'employees.byLabel': 'الدرجات حسب التصنيف',
  'employees.byLabelHint': 'لمن لهم درجة فقط.',
  'employees.label.EXCELLENT': 'ممتاز',
  'employees.label.VERY_GOOD': 'جيد جدًا',
  'employees.label.GOOD': 'جيد',
  'employees.label.NEEDS_ATTENTION': 'يحتاج إلى متابعة',
  'employees.method.title': 'كيف نحسب الدرجة',
  'employees.method.basis':
    'خمسة أبعاد: الإنجاز، والمواعيد النهائية، والجودة، والتذاكر، والتعاون. كل منها نسبة من أدلة عمل الشخص نفسه: المهام التي سلّمها، وخطوات سير العمل، ومهام الطابور، والتذاكر. والدرجة هي المتوسط المرجّح للأبعاد التي أمكن قياسها.',
  'employees.method.excluded': 'لا يدخل في الدرجة شيء غير ذلك: لا الحضور، ولا الورديات، ولا الورديات الحرة، ولا الإجازات، ولا وقت الخمول، ولا الأجر، ولا نقاط المحفظة، ولا الذكاء الاصطناعي.',
  'employees.method.nullScore': 'إذا كانت الأدلة قليلة، أو كان الجزء القابل للقياس من الوزن قليلًا، فلا تظهر درجة أصلًا، بل «لا توجد أدلة كافية»، ولا نفرض أبدًا درجة 0 أو 100.',
  'employees.method.weights': 'الأوزان هي إعدادات اللوحة السارية في أول يوم من الفترة (النسخة {version}). ويمكن أن يكون لقسم أوزانه الخاصة، ويمكن إيقاف أحد الأبعاد:',
  'employees.tableTitle': 'الموظفون حسب القسم',
  'employees.notCompared': 'اقرأ الدرجات داخل القسم الواحد، ولا تقارنها بين الأقسام أبدًا، فلكل قسم عمل مختلف.',
  'employees.noDepartment': 'بلا قسم',
  'employees.groupCount': '{n} في هذا القسم',
  'employees.empty': 'لا يوجد موظفون لهذه الفترة',
  'employees.emptyHint': 'لا أحد يطابق البحث. جرّب قسمًا أو اسمًا أو فترة أخرى.',
  'employees.col.departments': 'الأقسام',
  'employees.col.score': 'الدرجة',
  'employees.col.confidence': 'الثقة',
  'employees.col.evidence': 'الأدلة',
  'employees.col.measurable': 'الوزن القابل للقياس',
  'employees.col.tasks': 'المهام',
  'employees.col.steps': 'خطوات سير العمل',
  'employees.col.qTasks': 'مهام الطابور',
  'employees.col.tickets': 'التذاكر',
  'employees.col.missed': 'فائتة',
  'employees.confidence.HIGH': 'عالية',
  'employees.confidence.MEDIUM': 'متوسطة',
  'employees.confidence.LOW': 'منخفضة',
  'employees.confidence.NONE': 'لا توجد',
  'employees.reason.INSUFFICIENT_EVIDENCE': 'أحداث عمل قليلة جدًا',
  'employees.reason.INSUFFICIENT_COVERAGE': 'وزن قابل للقياس قليل جدًا',
  'employees.reason.INSUFFICIENT_EVIDENCE.long': 'أحداث العمل أقل مما تشترطه الإعدادات قبل إظهار رقم.',
  'employees.reason.INSUFFICIENT_COVERAGE.long': 'كانت الأدلة تغطي جزءًا قليلًا جدًا من وزن الأبعاد، ولم نتمكن من قياس الباقي.',
  'employees.revision': 'النسخة {n}',

  /* ---------------------------------------------------------------- One employee ------------ */
  'employee.back': 'كل الموظفين',
  'employee.primaryDepartment': 'القسم الأساسي',
  'employee.tab.performance': 'الأداء',
  'employee.tab.evidence': 'الأدلة',
  'employee.tab.points': 'النقاط',
  'employee.noRow': 'لا يوجد تقييم لهذه الفترة',
  'employee.noRowHint': 'لم يكن قد انضم حتى نهاية هذه الفترة.',
  'employee.score.title': 'درجة العمل',
  'employee.score.subtitle': 'من 100، محسوبة من أدلة العمل في الفترة',
  'employee.score.revision': 'النسخة',
  'employee.score.notStored': 'غير محفوظ، محسوب الآن',
  'employee.score.settingsVersion': 'نسخة الإعدادات',
  'employee.score.labels': 'التصنيفات: «ممتاز» من {excellent}، و«جيد جدًا» من {veryGood}، و«جيد» من {good}، وأقل من {good} «يحتاج إلى متابعة».',
  'employee.score.confidenceHint': 'الثقة هي كمية الأدلة ونسبة الوزن التي غطّتها، وليست أبدًا مدى يقين نموذج ذكاء اصطناعي.',
  'employee.previous.title': 'الفترة السابقة',
  'employee.previous.none': 'لا يوجد تقييم للفترة السابقة.',
  'employee.previous.change': 'التغيّر في الدرجة: {value}',
  'employee.dimensions.title': 'الأبعاد',
  'employee.dimensions.subtitle': 'كل منها نسبة من أدلة الشخص نفسه. والبعد الذي لا توجد له بيانات لحساب النسبة غير قابل للقياس، ولا وزن له.',
  'employee.dimensions.dimension': 'البعد',
  'employee.dimensions.weight': 'الوزن',
  'employee.dimensions.ratio': 'النسبة',
  'employee.dimensions.score': 'الدرجة',
  'employee.dimensions.formula': 'طريقة القياس',
  'employee.dimensions.off': 'متوقف',
  'employee.dimensions.notMeasurable': 'غير قابل للقياس، لا توجد بيانات لحسابه',
  'employee.dimensions.note': 'الدرجة هي المتوسط المرجّح للأبعاد القابلة للقياس، بعد إعادة حساب أوزانها على أساس الجزء الذي أمكن قياسه.',
  'employee.metrics.title': 'الأعداد',
  'employee.metrics.subtitle': 'مجموع أدلة الشخص خلال الفترة.',
  'employee.metrics.delivered': 'ما سلّمه',
  'employee.metrics.deadlines': 'المواعيد النهائية',
  'employee.metrics.review': 'مراجعة عمله',
  'employee.metrics.tickets': 'التذاكر',
  'employee.metrics.collaboration': 'المراجعات التي أجراها',
  'employee.metric.tasksDelivered': 'مهام سلّمها',
  'employee.metric.workflowSteps': 'خطوات سير عمل منجزة',
  'employee.metric.qTasksDone': 'مهام طابور منجزة',
  'employee.metric.ticketsResolved': 'تذاكر محلولة',
  'employee.metric.ticketsReopened': 'تم حلها ثم إعادة فتحها',
  'employee.metric.finishedOnTime': 'منجزة في موعدها',
  'employee.metric.finishedLate': 'منجزة متأخرة',
  'employee.metric.dueMissed': 'مواعيد نهائية فائتة',
  'employee.metric.approved': 'اعتمدها مراجع',
  'employee.metric.returned': 'رجعت للتعديل',
  'employee.metric.awaitingReview': 'بانتظار المراجعة',
  'employee.metric.selfCompleted': 'منجزة دون مراجعة',
  'employee.metric.viewSlaOnTime': 'تذاكر اطّلع عليها في موعدها',
  'employee.metric.viewSlaLate': 'تذاكر لم يطّلع عليها في موعدها',
  'employee.metric.reviewsDone': 'مراجعات أجراها',
  'employee.metric.reviewsDoneLate': 'مراجعات أجراها متأخرًا',
  'employee.metric.reviewsOverdue': 'مراجعات لا تزال تنتظره',
  'employee.metric.blocked': 'عمل معلّق',
  'employee.metric.blockedHint': 'للسياق فقط، ولا يدخل في الدرجة',
  'employee.findings.title': 'النتائج',
  'employee.findings.subtitle': 'ما تظهره الأدلة. كل نتيجة محسوبة من السجلات، ولم يكتبها الذكاء الاصطناعي.',
  'employee.findings.tone.POSITIVE': 'سار جيدًا',
  'employee.findings.tone.CAUTION': 'يستحق النظر',
  'employee.findings.tone.UNCERTAIN': 'غير مؤكد',
  'employee.findings.none': 'لا توجد نتائج لهذه الفترة.',
  'employee.findings.noneOfTone': 'لا شيء.',
  'employee.findings.seeEvidence': 'عرض الأدلة',
  'employee.flags.title': 'السياق',
  'employee.flags.subtitle': 'اقرأ الدرجة مع مراعاة ما يلي.',
  'employee.flags.none': 'لا توجد ملاحظات.',
  'employee.trend.title': 'الاتجاه',
  'employee.trend.byDay': 'درجة كل يوم، من أدلة ذلك اليوم وحده',
  'employee.trend.byMonth': 'درجة كل شهر، من أدلة ذلك الشهر وحده',
  'employee.trend.score': 'الدرجة',
  'employee.trend.month': 'الشهر',
  'employee.trend.gaps': 'الفجوة في الخط تعني يومًا (أو شهرًا) بلا أدلة كافية لحساب درجة، وليست صفرًا.',
  'employee.trend.summary': 'توجد درجة في {scored} من أصل {total}، أقلها {min} وأعلاها {max}.',
  'employee.trend.allNa': 'لا يوجد يوم (أو شهر) في هذه الفترة له أدلة كافية لحساب درجة.',
  'employee.trend.emptyHint': 'لا توجد بيانات لرسمها في هذه الفترة.',
  'employee.trend.table': 'عرض الأرقام في جدول',
  'employee.revisions.title': 'التقييمات المحفوظة',
  'employee.revisions.subtitle': 'نحفظ تقييم اليوم أو الشهر المغلق مع تقريره، وأي تغيّر في مصادره ينشئ نسخة جديدة، ولا نكتب فوق القديمة أبدًا.',
  'employee.revisions.revision': 'النسخة',
  'employee.revisions.current': 'الحالي',
  'employee.revisions.why': 'السبب',
  'employee.revisions.first': 'التقييم الأول',
  'employee.revisions.reason.SOURCES_CHANGED': 'تغيّرت مصادره',
  'employee.revisions.computed': 'وقت الحساب',
  'employee.revisions.finalized': 'صار نهائيًا',
  'employee.revisions.notStoredKind': 'نحفظ التقييمات للأيام والأشهر فقط. اختر يومًا أو شهرًا لعرضها.',
  'employee.revisions.live': 'هذه الفترة لم تنته بعد، وسنحفظ تقييمها عندما تنتهي.',
  'employee.revisions.none': 'لم نحفظ شيئًا لهذه الفترة بعد، وسنحفظه مع تقرير الفترة.',
  'employee.ai.title': 'شرح الذكاء الاصطناعي',
  'employee.ai.ask': 'اطلب شرحًا من الذكاء الاصطناعي',
  'employee.ai.askAgain': 'اطلب الشرح من جديد',
  'employee.ai.requested': 'تم طلب الشرح من الذكاء الاصطناعي.',
  'employee.ai.closedOnly': 'يشرح الذكاء الاصطناعي اليوم أو الشهر المغلق فقط. اختر يومًا أو شهرًا ماضيًا لتطلب الشرح.',
  'employee.ai.notStored': 'لا يوجد تقييم محفوظ لهذه الفترة بعد. يمكن للذكاء الاصطناعي شرحه بعد أن يحفظه تقرير الفترة.',
  'employee.ai.neverScore': 'الذكاء الاصطناعي يشرح الأرقام المحفوظة فقط، ولا يضع درجة أو يغيّرها أبدًا.',

  /* ---------------------------------------------------------------- Dimensions ------------- */
  'dimension.completion': 'الإنجاز',
  'dimension.deadline': 'المواعيد النهائية',
  'dimension.quality': 'الجودة',
  'dimension.tickets': 'التذاكر',
  'dimension.collaboration': 'التعاون',
  'dimension.completion.formula':
    'العناصر المنجزة (المهام المسلّمة، وخطوات سير العمل، ومهام الطابور المستقلة، والتذاكر المحلولة) ÷ (العناصر المنجزة + عناصره التي حلّ موعدها خلال الفترة ولا تزال مفتوحة).',
  'dimension.deadline.formula': 'المنجز في موعده النهائي أو قبله ÷ (المنجز الذي له موعد نهائي + ما لا يزال مفتوحًا بعد موعده).',
  'dimension.quality.formula':
    'عمله المسلّم الذي اعتمده شخص آخر ÷ (المعتمد + ما رجع للتعديل). هذا مؤشر وليس دليلًا على الجودة: العمل الذي لم يراجعه أحد لا يدخل في الحساب، ولا نعدّه إخفاقًا.',
  'dimension.tickets.formula':
    '(الاطلاعات الأولى ضمن مهلة اطلاع كان ملزمًا بها + الحلول التي بقيت محلولة) ÷ (تلك الاطلاعات المحسومة + الحلول). ومهلة الإسناد مسؤولية القسم، لا مسؤوليته.',
  'dimension.collaboration.formula':
    '(مراجعات عمل الآخرين التي أجراها ضمن الحد الزمني + خطوات سير العمل التي سلّمها في وقتها) ÷ (المراجعات التي أجراها + المراجعات التي ما زالت تنتظره بعد الحد الزمني + خطوات سير العمل التي لها موعد نهائي).',

  /* ---------------------------------------------------------------- Findings --------------- */
  'finding.DEADLINES_KEPT': 'من العناصر التي لها موعد نهائي، أنجز {onTime} من أصل {total} في موعدها.',
  'finding.ITEMS_PAST_DEADLINE': 'عناصر حلّ موعدها خلال الفترة ولا تزال مفتوحة: {count}.',
  'finding.FINISHED_LATE': 'عناصر أنجزها بعد موعدها النهائي: {count}.',
  'finding.WORK_APPROVED': 'من العناصر التي تمت مراجعتها، تم اعتماد {approved} من أصل {total}.',
  'finding.WORK_SENT_BACK': 'عناصر رجعت للتعديل: {count}، وهذا مؤشر يستحق النظر، وليس دليلًا.',
  'finding.TICKETS_REOPENED': 'تذاكر محلولة تمت إعادة فتحها: {count}.',
  'finding.VIEW_SLA_LATE': 'تذاكر لم يطّلع عليها ضمن مهلة الاطلاع: {count}.',
  'finding.STEADY_DELIVERY': 'عناصر أنجزها: {count}، ولم يتجاوز أي منها موعده النهائي.',
  'finding.REVIEWED_OTHERS': 'عناصر من عمل الآخرين راجعها: {count}.',
  'finding.REVIEWS_WAITING': 'مراجعات انتظرته بعد الحد الزمني: {count}.',
  'finding.BLOCKED_WORK': 'عناصر معلّقة لديه: {count}، وهي للسياق فقط ولا تؤثر على درجته.',
  'finding.AWAITING_REVIEW': 'عناصر سلّمها ولا تزال بانتظار المراجعة: {count}.',
  'finding.NEW_EMPLOYEE': 'انضم خلال آخر 30 يومًا من الفترة، فاقرأ الدرجة مع مراعاة ذلك.',
  'finding.OVERDUE_BACKLOG': 'عناصر كانت متأخرة قبل الفترة: {count}، وهي للسياق فقط ولا تدخل في الدرجة مرة ثانية.',
  'finding.DIMENSION_NOT_MEASURABLE': 'لم نتمكن من قياس «{dimension}»: لا توجد أدلة لحساب النسبة.',

  /* ---------------------------------------------------------------- Flags ------------------ */
  'flag.NEW_EMPLOYEE': 'موظف جديد',
  'flag.NEW_EMPLOYEE.hint': 'انضم خلال 30 يومًا قبل نهاية الفترة، ولا نصنّفه منخفضًا بسبب ذلك أبدًا.',
  'flag.BLOCKED_WORK': 'عمل معلّق',
  'flag.BLOCKED_WORK.hint': 'بعض عمله معلّق، وهذا للسياق فقط ولا يدخل في الدرجة أبدًا.',
  'flag.OVERDUE_BACKLOG': 'متأخرات سابقة',
  'flag.OVERDUE_BACKLOG.hint': 'عناصر متأخرة من قبل الفترة، ولا تدخل في الدرجة هنا مرة ثانية.',
  'flag.AWAITING_REVIEW': 'بانتظار المراجعة',
  'flag.AWAITING_REVIEW.hint': 'عمل سلّمه ولم يراجعه أحد بعد.',
  'flag.SELF_COMPLETED_UNREVIEWED': 'منجز دون مراجعة',
  'flag.SELF_COMPLETED_UNREVIEWED.hint': 'عمل أنهاه بنفسه دون مراجع يعتمده.',
  'flag.SMALL_SAMPLE': 'عيّنة صغيرة',
  'flag.SMALL_SAMPLE.hint': 'يقوم أحد الأبعاد على أقل من ثلاثة عناصر.',
  'flag.NO_DEADLINE_EVIDENCE': 'لا أدلة على المواعيد النهائية',
  'flag.NO_DEADLINE_EVIDENCE.hint': 'لم يكن لأي من عمله موعد نهائي خلال الفترة، لذلك لا يمكن قياس المواعيد النهائية.',

  /* ---------------------------------------------------------------- Evidence --------------- */
  'evidence.listNote': 'كل سجل تقوم عليه الدرجة، الأحدث أولًا: ما هو، ومتى، وما نتيجته. تظهر لك العناوين، لكن الذكاء الاصطناعي لا يراها أبدًا عند كتابة الشرح.',
  'evidence.empty': 'لا توجد أدلة في هذه الفترة',
  'evidence.emptyHint': 'لا يوجد له في هذه الفترة عمل منجز، ولا عمل حلّ موعده، ولا عمل تمت مراجعته.',
  'evidence.col.when': 'متى',
  'evidence.col.kind': 'الدليل',
  'evidence.col.record': 'السجل',
  'evidence.col.deadline': 'الموعد النهائي',
  'evidence.col.outcome': 'النتيجة',
  'evidence.col.verification': 'التحقق',
  'evidence.recordGone': 'السجل لم يعد متاحًا',
  'evidence.kind.TASK_DELIVERED': 'مهمة مسلّمة',
  'evidence.kind.WORKFLOW_STEP': 'خطوة سير عمل منجزة',
  'evidence.kind.QTASK_DONE': 'مهمة طابور منجزة',
  'evidence.kind.TICKET_RESOLVED': 'تذكرة محلولة',
  'evidence.kind.DUE_MISSED': 'موعد نهائي فائت',
  'evidence.kind.REVIEW_APPROVED': 'تم اعتماد عمله',
  'evidence.kind.REVIEW_RETURNED': 'رجع عمله للتعديل',
  'evidence.kind.VIEW_SLA': 'مهلة الاطلاع على تذكرة',
  'evidence.kind.REVIEW_DONE': 'راجع عمل شخص آخر',
  'evidence.kind.REVIEW_OVERDUE': 'مراجعة تنتظره',
  'evidence.kind.BLOCKED_CONTEXT': 'معلّق (للسياق)',
  'evidence.source.TASK': 'مهمة',
  'evidence.source.Q_TASK': 'مهمة طابور',
  'evidence.source.TICKET': 'تذكرة',
  'evidence.verification.APPROVED': 'اعتمدها مراجع',
  'evidence.verification.WORKFLOW_STEP': 'انتقلت ضمن سير عملها',
  'evidence.verification.AWAITING_REVIEW': 'بانتظار المراجعة',
  'evidence.verification.SELF_DONE': 'منجزة دون مراجعة',
  'evidence.outcome.onTime': 'في الموعد',
  'evidence.outcome.late': 'متأخر',
  'evidence.outcome.noDeadline': 'بلا موعد نهائي',
  'evidence.outcome.stayed': 'بقيت محلولة',
  'evidence.outcome.reopened': 'أعيد فتحها',
  'evidence.outcome.missed': 'فائت',
  'evidence.outcome.blocked': 'معلّق، لا يدخل في الدرجة',
  'evidence.outcome.approved': 'معتمد',
  'evidence.outcome.returned': 'رجع للتعديل',
  'evidence.outcome.reviewWaiting': 'تنتظر بعد الحد الزمني',
  'evidence.outcome.reviewInTime': 'راجعه في الموعد',
  'evidence.outcome.reviewLate': 'راجعه متأخرًا',
  'evidence.outcome.viewedInTime': 'اطّلع عليها في الموعد',
  'evidence.outcome.viewedLate': 'لم يطّلع عليها في الموعد',
  'evidence.back': 'رجوع',
  'evidence.badType': 'السجل هنا مهمة أو تذكرة أو مهمة طابور.',
  'evidence.item.summary': 'الملخص',
  'evidence.item.dates': 'التواريخ',
  'evidence.item.assignee': 'المسؤول',
  'evidence.item.createdBy': 'المنشئ',
  'evidence.item.wholeDepartment': 'القسم كله',
  'evidence.item.workflow': 'سير العمل',
  'evidence.item.noWorkflow': 'ليست ضمن سير عمل',
  'evidence.item.fromQTask': 'من مهمة طابور',
  'evidence.item.kindLabel': 'النوع',
  'evidence.item.leaderTask': 'مهمة قادة',
  'evidence.item.targetDepartment': 'القسم',
  'evidence.item.converted': 'تم تحويلها إلى مهام',
  'evidence.item.convertedYes': 'نعم، ومهامها هي التي تحمل العمل',
  'evidence.item.created': 'تم الإنشاء',
  'evidence.item.started': 'بدأت',
  'evidence.item.completed': 'اكتملت',
  'evidence.item.assigned': 'تم الإسناد',
  'evidence.item.resolved': 'تم حلها',
  'evidence.item.closed': 'تم إغلاقها',
  'evidence.item.viewDeadline': 'موعد الاطلاع',
  'evidence.item.assignmentDeadline': 'موعد الإسناد',
  'evidence.item.views': 'الاطلاعات الأولى',
  'evidence.item.viewsHint': 'من فتح التذكرة أولًا، وهل كان ملزمًا بمهلة الاطلاع عليها.',
  'evidence.item.noViews': 'لم يطّلع عليها أحد بعد.',
  'evidence.item.firstViewed': 'أول اطلاع',
  'evidence.item.eligible': 'ملزم بمهلة الاطلاع',
  'evidence.item.notJudged': 'غير محتسب',
  'evidence.item.becameTasks': 'المهام التي تحوّلت إليها',
  'evidence.item.becameTasksHint': 'مهمة الطابور المحوّلة تدخل في الحساب من خلال هذه المهام.',
  'evidence.item.taskN': 'المهمة {n}',
  'evidence.item.timeline': 'التسلسل الزمني',
  'evidence.item.timelineHint': 'الإجراءات التي تمت عليها، ومن قام بها، ومتى.',
  'evidence.item.noTimeline': 'لا توجد إجراءات مسجّلة.',
  'evidence.item.system': 'النظام',
  'evidence.item.noContent': 'للقراءة فقط: لا تعرض اللوحة وصفها ولا تعليقاتها ولا ملفاتها.',
  'evidence.action.task_status_changed': 'تغيّرت الحالة',
  'evidence.action.task_assigned': 'تم الإسناد',
  'evidence.action.task_reassigned': 'تمت إعادة الإسناد',
  'evidence.action.task_deadline_set': 'تم تحديد الموعد النهائي',
  'evidence.action.task_returned': 'تمت إعادتها',
  'evidence.action.task_workflow_handed_on': 'انتقلت إلى المستوى التالي',
  'evidence.action.ticket_status_changed': 'تغيّرت الحالة',
  'evidence.action.ticket_assigned': 'تم الإسناد',
  'evidence.action.ticket_reassigned': 'تمت إعادة الإسناد',
  'evidence.action.ticket_resolved': 'تم حلها',
  'evidence.action.ticket_reopened': 'أعيد فتحها',
  'evidence.action.ticket_claim': 'تم استلامها',
  'evidence.action.ticket_rerouted': 'تم نقلها إلى قسم آخر',
  'evidence.action.q_task_created': 'تم الإنشاء',
  'evidence.action.q_task_available': 'أصبحت متاحة',
  'evidence.action.q_task_claimed': 'تم استلامها',
  'evidence.action.q_task_assigned': 'تم الإسناد',
  'evidence.action.q_task_reassigned': 'تمت إعادة الإسناد',
  'evidence.action.q_task_released': 'عادت إلى الطابور',
  'evidence.action.q_task_force_released': 'أعادها قائد الفريق إلى الطابور',
  'evidence.action.q_task_started': 'بدأت',
  'evidence.action.q_task_blocked': 'تم تعليقها',
  'evidence.action.q_task_unblocked': 'تم رفع التعليق',
  'evidence.action.q_task_rescheduled': 'تغيّر الموعد النهائي',
  'evidence.action.q_task_completed': 'اكتملت',
  'evidence.action.q_task_skipped': 'تم تخطّيها',
  'evidence.action.q_task_cancelled': 'ملغاة',
  'evidence.action.q_task_converted': 'تم تحويلها إلى مهام',
  'evidence.action.q_task_done_by_tasks': 'اكتملت بعد انتهاء مهامها',
  'evidence.action.q_task_reopened_by_task': 'أعادت فتحها إحدى مهامها',
  'evidence.action.q_task_cancelled_by_tasks': 'تم إلغاؤها بعد إلغاء مهامها',

  /* ---------------------------------------------------------------- Points & Rewards ------- */
  'points.title': 'النقاط والمكافآت',
  'points.subtitle': 'سجل المحفظة نفسه: النقاط الممنوحة والمستبدلة والمعادة، والمكافآت والمبالغ المالية، وهو منفصل عن أداء العمل.',
  'points.allEmployees': 'كل الموظفين',
  'points.employeeWins': 'اخترت موظفًا، لذلك لا نطبق فلتر القسم.',
  'points.separate': 'النقاط ليست درجة الأداء: لا تتحول أي درجة إلى نقاط هنا، ولا تدخل أي نقطة في حساب الدرجة.',
  'points.private': 'سبب منح النقاط ومن منحها يبقيان عند مديري النظام فقط، ولا نرسلهما إلى هذه اللوحة أبدًا.',
  'points.daysOnly': 'نعرض مكافآت الأيام بعدد الأيام، لا بالمال أبدًا، لأن مكافأة اليوم هي يوم من أجر شخص ما.',
  'points.totals.granted': 'الممنوحة',
  'points.totals.grantedHint': 'النقاط المضافة خلال الفترة',
  'points.totals.redeemed': 'المستبدلة',
  'points.totals.redeemedHint': 'النقاط المصروفة على المكافآت',
  'points.totals.refunded': 'المعادة',
  'points.totals.refundedHint': 'النقاط التي رجعت من عمليات استبدال ملغاة',
  'points.totals.net': 'الصافي',
  'points.totals.netHint': 'الممنوحة − المستبدلة + المعادة',
  'points.bySource.title': 'الممنوحة حسب المصدر',
  'points.bySource.subtitle': 'من أين جاءت نقاط الفترة.',
  'points.source.AI_DAILY_POINTS': 'نقاط الأداء اليومية',
  'points.source.ADMINISTRATOR': 'أحد مديري النظام',
  'points.source.OTHER': 'أخرى',
  'points.balances.title': 'الأرصدة الآن',
  'points.balances.subtitle': 'أرصدة اليوم، أيًّا كانت الفترة.',
  'points.balances.total': 'مجموع النقاط في المحافظ',
  'points.balances.wallets': 'المحافظ',
  'points.balances.withPoints': 'محافظ فيها نقاط',
  'points.trend.title': 'النقاط عبر الزمن',
  'points.trend.byDay': 'حسب اليوم',
  'points.trend.byMonth': 'حسب الشهر',
  'points.trend.empty': 'لم تتحرك أي نقاط في هذه الفترة',
  'points.trend.summary': 'خلال الفترة: الممنوحة {granted}، والمستبدلة {redeemed}، والمعادة {refunded}.',
  'points.trend.table': 'عرض الأرقام في جدول',
  'points.redemptions.byType': 'الاستبدالات حسب النوع والحالة',
  'points.redemptions.byTypeHint': 'عدد الاستبدالات خلال الفترة، والنقاط التي كلّفتها.',
  'points.redemptions.title': 'الاستبدالات',
  'points.redemptions.subtitle': 'لكل استبدال تاريخه، ولصرف المكافأة المالية تاريخ خاص به أيضًا.',
  'points.redemptions.empty': 'لا توجد استبدالات في هذه الفترة',
  'points.redemptions.type': 'المكافأة',
  'points.redemptions.redeemedAt': 'تاريخ الاستبدال',
  'points.redemptions.completedAt': 'تاريخ الإتمام',
  'points.redemptions.cash': 'المبلغ المالي',
  'points.redemptions.cancelledOn': 'تم الإلغاء في {date}',
  'points.redemption.REMOVE_QUARTER_DAY_PENALTY': 'إلغاء خصم ربع يوم',
  'points.redemption.REMOVE_HALF_DAY_PENALTY': 'إلغاء خصم نصف يوم',
  'points.redemption.REMOVE_FULL_DAY_PENALTY': 'إلغاء خصم يوم كامل',
  'points.redemption.ADD_ONE_LEAVE_DAY': 'إضافة يوم إجازة',
  'points.redemption.CASH_REWARD': 'استبدال النقاط بمبلغ مالي',
  'points.redemptionStatus.COMPLETED': 'تمت',
  'points.redemptionStatus.PENDING_SETTLEMENT': 'قيد الصرف',
  'points.redemptionStatus.CANCELLED': 'ملغاة',
  'points.cashStatus.PENDING': 'لم يتم صرفها بعد',
  'points.cashStatus.PAID': 'تم الصرف',
  'points.cashStatus.CANCELLED': 'ملغاة',
  'points.dayRewardStatus.GRANTED': 'بانتظار الصرف',
  'points.dayRewardStatus.PAID': 'تم الصرف',
  'points.dayRewardStatus.CANCELLED': 'مسحوبة',
  'points.kind.CREDIT': 'تمت إضافة نقاط',
  'points.kind.REDEMPTION_DEBIT': 'استبدال',
  'points.kind.REFUND_CREDIT': 'إرجاع نقاط من استبدال',
  'points.pointsValue': 'النقاط: {n}',
  'points.cash.title': 'المكافآت المالية حسب العملة',
  'points.cash.subtitle': 'كل مبلغ بعملته.',
  'points.cash.empty': 'لا توجد مكافآت مالية في هذه الفترة',
  'points.cash.requested': 'المطلوب',
  'points.cash.paid': 'المصروف',
  'points.cash.cancelled': 'الملغى',
  'points.cash.pending': 'ما زال قيد الصرف',
  'points.cash.note': '«المطلوب» يدخل في يوم طلبه، و«المصروف» لا يدخل إلا بعد صرفه وفي يوم صرفه، و«ما زال قيد الصرف» هو ما تم طلبه خلال الفترة ولم يتم صرفه بعد.',
  'points.cash.paidOn': 'تم الصرف في {date}',
  'points.cash.notPaid': 'لم يتم الصرف بعد',
  'points.dayRewards.title': 'مكافآت الأيام',
  'points.dayRewards.subtitle': 'بعدد الأيام، وليس بقيمتها من الأجر أبدًا.',
  'points.dayRewards.summarySubtitle': 'الممنوحة خلال الفترة، بعدد الأيام، وليس بقيمتها من الأجر أبدًا.',
  'points.dayRewards.empty': 'لا توجد مكافآت أيام في هذه الفترة',
  'points.dayRewards.days': 'الأيام',
  'points.dayRewards.month': 'شهر الرواتب',
  'points.dayRewards.grantedAt': 'تاريخ المنح',
  'points.dayRewards.settled': 'تاريخ الصرف أو السحب',
  'points.dayRewards.rewards': 'المكافآت',
  'points.dayRewards.totalDays': 'مجموع الأيام',
  'points.dayRewards.parts': 'تتكون من',
  'points.dayRewards.part': '{n} × {days} يوم',
  'points.employees.title': 'الموظفون',
  'points.employees.subtitle': 'النقاط التي حصل عليها كل شخص واستبدلها واستردها خلال الفترة، ورصيده الآن.',
  'points.employees.empty': 'لم تتحرك أي محفظة في هذه الفترة',
  'points.employees.balance': 'الرصيد الآن',
  'points.balance.current': 'الرصيد الآن',
  'points.balance.currentHint': 'اليوم، أيًّا كانت الفترة',
  'points.balance.opening': 'في بداية الفترة',
  'points.balance.openingHint': 'قبل أول قيد فيها',
  'points.balance.closing': 'في نهاية الفترة',
  'points.balance.closingHint': 'بعد آخر قيد فيها',
  'points.ledger.title': 'السجل',
  'points.ledger.subtitle': 'كل القيود خلال الفترة، الأقدم أولًا، دون ذكر السبب.',
  'points.ledger.empty': 'لا توجد قيود في هذه الفترة',
  'points.ledger.entry': 'القيد',
  'points.ledger.source': 'المصدر',
  'points.ledger.points': 'النقاط',
  'points.ledger.balanceAfter': 'الرصيد بعده',

  /* ---------------------------------------------------------------- Work & Productivity ---- */
  'work.title': 'العمل والإنتاجية',
  'work.subtitle': 'المهام والتذاكر ومهام الطابور وسير العمل، ونحسب كل عنصر عمل مرة واحدة.',
  'work.allProjects': 'كل المشاريع',
  'work.tab.summary': 'الملخص',
  'work.tab.departments': 'الأقسام',
  'work.tab.workload': 'عبء العمل',
  'work.na': 'غير متاح',
  'work.hours': '{n} ساعة',
  'work.days': '{n} يوم',
  'work.now': 'الآن',
  'work.nowHint': 'الآن',
  'work.inPeriod': 'خلال الفترة',
  'work.inPeriodHint': 'خلال الفترة',
  'work.countedOnce':
    'نحسب كل عنصر عمل مرة واحدة: سير العمل عنصر أساسي واحد عندما يكتمل، ومهامه هي خطواته. ومهمة الطابور المحوّلة إلى مهام نحسبها من خلال تلك المهام. والمهمة التي يتشاركها قسم كامل مهمة واحدة. أما مهام القادة فنحسبها على حدة.',
  'work.firstOf': 'نعرض أول {shown} من أصل {total}.',
  'work.projectFilterNote': 'مهام الطابور والمخالفات ومهام القادة لا تتبع أي مشروع، لذلك عند اختيار مشروع تبقى أرقامها هي أرقام القسم (أو الشركة).',
  'work.daysBlocked': 'أيام التعليق: {n}',
  'work.tasks.title': 'المهام',
  'work.tasks.subtitle': 'مهام القادة غير محسوبة هنا، وتجدها أدناه.',
  'work.tasks.uniqueRoot': 'عناصر أساسية فريدة مكتملة',
  'work.tasks.uniqueRootHint': 'المهام خارج سير العمل، وسير العمل المكتمل، وكل منها مرة واحدة',
  'work.tasks.steps': 'خطوات سير عمل مكتملة',
  'work.tasks.stepsHint': 'خطوات، لا عناصر أساسية مستقلة',
  'work.tasks.completed': 'مهام منجزة خارج سير العمل',
  'work.tasks.fromQTasks': 'منها من مهام الطابور: {n}',
  'work.tasks.created': 'تم الإنشاء',
  'work.tasks.cancelled': 'ملغاة',
  'work.tasks.newlyOverdue': 'تأخرت',
  'work.tasks.newlyOverdueHint': 'فات موعدها خلال الفترة ولا تزال مفتوحة',
  'work.tasks.open.toDo': 'قيد الانتظار',
  'work.tasks.open.inProgress': 'قيد التنفيذ',
  'work.tasks.open.blocked': 'معلّقة',
  'work.tasks.open.review': 'قيد المراجعة',
  'work.tasks.overdue': 'متأخرة',
  'work.tasks.overdueHint': 'مفتوحة بعد موعدها النهائي (ليست قيد المراجعة)',
  'work.tasks.dueSoon': 'موعدها خلال 48 ساعة',
  'work.tasks.dueSoonHint': 'مفتوحة، ليست قيد المراجعة',
  'work.tasks.cycle.title': 'زمن الدورة — الوقت المنقضي، لا وقت العمل',
  'work.tasks.cycle.median': 'الوسيط',
  'work.tasks.cycle.p90': 'المئين التسعون',
  'work.tasks.cycle.sample': 'عدد المهام المقاسة',
  'work.tasks.cycle.note': 'من الإنشاء حتى الإنجاز، بالوقت المنقضي على الساعة، بما في ذلك الليالي وعطلات نهاية الأسبوع وأوقات الانتظار. ولا يسجّل النظام الوقت الذي عمل فيه الشخص فعلًا.',
  'work.tickets.title': 'التذاكر',
  'work.tickets.subtitle': 'التذكرة التي تم حلها مرتين هي تذكرة واحدة وحلّان.',
  'work.tickets.created': 'تم الإنشاء',
  'work.tickets.resolvedTickets': 'تذاكر محلولة',
  'work.tickets.resolvedTicketsHint': 'كل تذكرة مرة واحدة',
  'work.tickets.resolutionEvents': 'مرات الحل',
  'work.tickets.resolutionEventsHint': 'كل مرة تم فيها حل تذكرة',
  'work.tickets.reopenEvents': 'مرات إعادة الفتح',
  'work.tickets.reopenRate': 'معدل إعادة الفتح',
  'work.tickets.reopenRateHint': 'مرات إعادة الفتح ÷ مرات الحل',
  'work.tickets.closed': 'تم إغلاقها',
  'work.tickets.viewSla': 'الالتزام بمهلة الاطلاع',
  'work.tickets.assignmentSla': 'الالتزام بمهلة الإسناد',
  'work.tickets.slaHint': '{onTime} من أصل {decided} محسومة',
  'work.tickets.slaNone': 'لا شيء محسوم بعد',
  'work.tickets.open.new': 'جديدة',
  'work.tickets.open.assigned': 'مسندة',
  'work.tickets.open.inProgress': 'قيد التنفيذ',
  'work.tickets.open.waiting': 'بانتظار مقدّم الطلب',
  'work.tickets.open.reopened': 'أعيد فتحها',
  'work.tickets.urgentOpen': 'عاجلة ومفتوحة',
  'work.tickets.highOpen': 'عالية ومفتوحة',
  'work.tickets.urgentHighUnassigned': 'عاجلة أو عالية وغير مسندة',
  'work.tickets.unassignedPastDeadline': 'غير مسندة بعد موعد الإسناد',
  'work.tickets.openViolations': 'مخالفات مفتوحة',
  'work.tickets.openViolationsHint': 'تم تسجيلها خلال الفترة ولا تزال مفتوحة',
  'work.qTasks.title': 'مهام الطابور',
  'work.qTasks.subtitle': 'نحسب مهمة الطابور المحوّلة من خلال مهامها، ونحسب المستقلة المنجزة مرة واحدة.',
  'work.qTasks.available': 'متاحة',
  'work.qTasks.claimed': 'مستلمة',
  'work.qTasks.inProgress': 'قيد التنفيذ',
  'work.qTasks.blocked': 'معلّقة',
  'work.qTasks.queueAge': 'وسيط مدة الانتظار',
  'work.qTasks.queueAgeHint': 'للمهام المتاحة الآن',
  'work.qTasks.doneStandalone': 'منجزة (مستقلة)',
  'work.qTasks.doneStandaloneHint': 'لم يتم تحويلها إلى مهام',
  'work.qTasks.converted': 'تم تحويلها إلى مهام',
  'work.qTasks.convertedHint': 'نحسبها من خلال مهامها',
  'work.qTasks.skipped': 'تم تخطّيها',
  'work.qTasks.longBlocked': 'معلّقة مدة أطول مما تسمح به الإعدادات: {n}',
  'work.qTasks.longBlockedNone': 'لا شيء.',
  'work.workflows.title': 'سير العمل',
  'work.workflows.subtitle': 'سير العمل عنصر أساسي واحد، والمهام فيه هي خطواته.',
  'work.workflows.active': 'نشطة',
  'work.workflows.completed': 'مكتملة',
  'work.workflows.stopped': 'متوقفة',
  'work.workflows.steps': 'خطوات مكتملة',
  'work.workflows.stalled': 'متعثرة، بلا حركة مدة أطول مما تسمح به الإعدادات: {n}',
  'work.workflows.stalledNone': 'لا شيء.',
  'work.workflows.level': 'المستوى {n}',
  'work.workflows.hasBlocked': 'إحدى خطواته معلّقة',
  'work.workflows.idle': 'أيام بلا حركة: {n}',
  'work.leaders.title': 'مهام القادة',
  'work.leaders.subtitle': 'نحسبها على حدة، أما العمل الذي يتم إسناده منها فيدخل في أرقام المهام.',
  'work.leaders.open': 'مفتوحة',
  'work.leaders.done': 'منجزة',
  'work.departments.note': 'نقارن كل قسم بفترته السابقة فقط، وهذا ليس ترتيبًا للأقسام، فلكل قسم عمل مختلف.',
  'work.departments.empty': 'لا توجد أقسام لعرضها',
  'work.departments.chartTitle': 'العمل المتراكم والمكتمل',
  'work.departments.chartSubtitle': 'العناصر المفتوحة الآن، والعناصر المكتملة في هذه الفترة والفترة السابقة.',
  'work.departments.chartLabel': 'العمل المتراكم، والعمل المكتمل في هذه الفترة والفترة السابقة، لعدد {n} من الأقسام. كل الأرقام في الجدول أدناه.',
  'work.departments.chartTwin': 'كل أرقام الرسم موجودة في الجدول أدناه.',
  'work.departments.backlog': 'المتراكم الآن',
  'work.departments.completed': 'المكتمل',
  'work.departments.previous': 'المكتمل في الفترة السابقة',
  'work.departments.members': 'الأعضاء',
  'work.departments.openTasks': 'مهام مفتوحة',
  'work.departments.openTickets': 'تذاكر مفتوحة',
  'work.departments.openQTasks': 'مهام طابور مفتوحة',
  'work.departments.overdue': 'مهام متأخرة',
  'work.departments.doneTasks': 'مهام منجزة',
  'work.departments.doneTickets': 'تذاكر محلولة',
  'work.departments.doneQTasks': 'مهام طابور منجزة',
  'work.departments.change': 'التغيّر',
  'work.workload.note': 'العمل المفتوح لدى كل شخص الآن، مع مواعيده النهائية. هذه ليست «نسبة انشغال»، لأن النظام لا يسجّل تقديرات للوقت، فلا يمكنه معرفة مدى انشغال أي شخص.',
  'work.workload.empty': 'لا يوجد عمل مفتوح لدى أحد',
  'work.workload.openTasks': 'مهام مفتوحة',
  'work.workload.overdue': 'متأخرة',
  'work.workload.dueSoon': 'موعدها خلال 48 ساعة',
  'work.workload.openTickets': 'تذاكر مفتوحة',
  'work.workload.openQTasks': 'مهام طابور مفتوحة',
  'work.workload.total': 'العناصر المفتوحة',
  'work.workload.limit': 'نعرض أكثر 100 شخص لديهم عمل مفتوح.',

  /* ---------------------------------------------------------------- Projects --------------- */
  'projects.title': 'المشاريع والعملاء',
  'projects.subtitle': 'سلامة التسليم محسوبة من مهام كل مشروع وتواريخه، وسلامة الخدمة من المهل والتذاكر التي تمت إعادة فتحها.',
  'projects.tab.projects': 'المشاريع',
  'projects.tab.clients': 'العملاء',
  'projects.searchPlaceholder': 'ابحث بالاسم أو الرمز',
  'projects.allStatuses': 'كل الحالات',
  'projects.allRisks': 'كل المستويات',
  'projects.allClients': 'كل العملاء',
  'projects.count.total': 'المشاريع',
  'projects.count.delivering': 'قيد التسليم',
  'projects.count.deliveringHint': 'مسودة أو نشط أو قيد الاختبار أو متوقف',
  'projects.count.support': 'قيد الدعم',
  'projects.count.atRisk': 'معرّض للخطر',
  'projects.count.atRiskHint': 'مخاطر تسليم عالية',
  'projects.count.watch': 'تحت المراقبة',
  'projects.count.watchHint': 'مخاطر تسليم متوسطة',
  'projects.countsBeforeRisk': 'الأعداد أعلاه قبل تطبيق فلتر المستوى.',
  'projects.empty': 'لا توجد مشاريع مطابقة',
  'projects.col.risk': 'مخاطر التسليم',
  'projects.col.progress': 'التقدم',
  'projects.col.overdueBlocked': 'متأخرة · معلّقة',
  'projects.col.openTickets': 'تذاكر مفتوحة',
  'projects.col.support': 'تغطية الدعم',
  'projects.col.invoiced': 'المفوتر حتى الآن',
  'projects.col.collected': 'المحصّل حتى الآن',
  'projects.col.outstanding': 'المستحق الآن',
  'projects.risk.HIGH': 'معرّض للخطر',
  'projects.risk.MEDIUM': 'تحت المراقبة',
  'projects.risk.LOW': 'على المسار الصحيح',
  'projects.risk.NONE': 'بلا تقييم',
  'projects.reason.PAST_EXPECTED_END': 'تجاوز موعد انتهائه المتوقع، ولا يزال فيه عمل مفتوح',
  'projects.reason.OVERDUE_TASKS': 'ثلاث مهام متأخرة أو أكثر',
  'projects.reason.OVERDUE_SHARE': 'جزء كبير من مهامه ذات المواعيد النهائية متأخر',
  'projects.reason.OVERDUE_TASK': 'مهمة متأخرة',
  'projects.reason.BLOCKED_TASKS': 'مهمتان معلّقتان أو أكثر',
  'projects.reason.ON_HOLD': 'متوقف',
  'projects.reason.NO_TRACKED_TASKS': 'لا مهام متتبّعة ولا تاريخ انتهاء',
  'projects.progress': 'اكتملت {done} من أصل {tracked} من المهام المتتبّعة',
  'projects.noTrackedTasks': 'لا توجد مهام متتبّعة',
  'projects.noInvoices': 'لا توجد فواتير',
  'projects.noRate': 'لا يوجد سعر صرف لـ{currencies}، لذلك استبعدنا مبالغها، ولا نحسبها صفرًا أبدًا.',
  'projects.supportUntil': 'حتى {date}',
  'projects.noSupport': 'لا يوجد عقد يغطيه اليوم',
  'projects.progressNote': 'التقدم هو عدد المهام المتتبّعة المكتملة، وليس نسبة مئوية من المشروع أبدًا، لأن المشروع ليس له نطاق أو مراحل يمكن قياسها.',
  'projects.moneyNote': 'فواتير المالية المستحقة على العملاء التي تذكر المشروع، حتى الآن، ولا تقتصر على الفترة.',
  'projects.marginNote': 'هامش الربح غير متاح، لأن التكاليف لا ترتبط بالمشاريع.',
  'projects.back': 'المشاريع والعملاء',
  'projects.beyondList': 'هذا المشروع خارج الحد الذي تقرؤه اللوحة في المرة الواحدة.',
  'projects.owner': 'المسؤول',
  'projects.detail.overdueTasks': 'مهام متأخرة',
  'projects.detail.openBlocked': 'مفتوحة: {open} · قيد المراجعة: {review} · معلّقة: {blocked}',
  'projects.detail.resolvedInPeriod': 'تم حلها خلال الفترة: {n}',
  'projects.detail.money': 'المال',
  'projects.detail.margin': 'هامش الربح',
  'projects.detail.dates': 'التواريخ',
  'projects.detail.start': 'البداية',
  'projects.detail.expectedEnd': 'الانتهاء المتوقع',
  'projects.detail.actualEnd': 'الانتهاء الفعلي',
  'projects.detail.end': 'النهاية',
  'projects.detail.contract': 'العقد',
  'projects.detail.until': 'يغطي حتى',
  'projects.detail.contracts': 'العقود',
  'projects.detail.noContracts': 'لا يوجد عقد يذكر هذا المشروع.',
  'projects.detail.billing': 'الفوترة',
  'projects.detail.overdueList': 'مهام متأخرة',
  'projects.detail.overdueListHint': 'مفتوحة بعد موعدها النهائي، الأقدم أولًا.',
  'projects.detail.noOverdue': 'لا توجد مهام متأخرة.',
  'projects.detail.blockedList': 'مهام معلّقة',
  'projects.detail.blockedListHint': 'الأطول دون تغيير أولًا.',
  'projects.detail.noBlocked': 'لا توجد مهام معلّقة.',
  'projects.detail.ticketsList': 'تذاكر مفتوحة',
  'projects.detail.ticketsListHint': 'الأكثر إلحاحًا أولًا.',
  'projects.detail.noTickets': 'لا توجد تذاكر مفتوحة.',
  'projects.detail.task': 'المهمة',
  'projects.detail.ticket': 'التذكرة',
  'projects.detail.opened': 'تاريخ الفتح',
  'projects.detail.lastChange': 'آخر تغيير',
  'projects.detail.capped': 'نعرض أول {n} فقط.',
  'projects.detail.weeklyTitle': 'المهام المتتبّعة المكتملة حسب الأسبوع',
  'projects.detail.weeklySubtitle': 'آخر 12 أسبوعًا، يبدأ كل منها من أول أيام الأسبوع في الشركة، أيًّا كانت الفترة.',
  'projects.detail.weeklySeries': 'مهام مكتملة',
  'projects.detail.weeklyNone': 'لم تكتمل أي مهمة متتبّعة خلال آخر 12 أسبوعًا.',
  'projects.detail.weeklyLabel': 'المهام المتتبّعة المكتملة: {n}، وعدد الأسابيع: {weeks}.',
  'projects.detail.weeklyTable': 'عرض الأرقام في جدول',
  'projects.detail.week': 'الأسبوع',
  'projects.detail.weekOf': 'أسبوع {date}',

  /* ---------------------------------------------------------------- Clients ---------------- */
  'clients.searchPlaceholder': 'ابحث بالاسم أو الرمز',
  'clients.serviceNote': 'سلامة الخدمة هي الالتزام بالمهل والتذاكر التي تمت إعادة فتحها. لا يوجد استبيان لرضا العملاء، لذلك لا توجد درجة رضا.',
  'clients.tableTitle': 'العملاء',
  'clients.tableSubtitle': 'التذاكر والتحصيل خلال الفترة، وما عليهم من مستحقات الآن.',
  'clients.empty': 'لا يوجد عملاء مطابقون',
  'clients.col.projects': 'المشاريع',
  'clients.col.openTickets': 'تذاكر مفتوحة',
  'clients.col.resolved': 'تم حلها خلال الفترة',
  'clients.col.reopened': 'تمت إعادة فتحها خلال الفترة',
  'clients.col.assignmentSla': 'الالتزام بمهلة الإسناد',
  'clients.col.viewSla': 'الالتزام بمهلة الاطلاع',
  'clients.col.collected': 'المحصّل خلال الفترة',
  'clients.col.outstanding': 'المستحق الآن',
  'clients.col.overdueInvoices': 'فواتير متأخرة',
  'clients.col.contracts': 'العقود',
  'clients.delivering': 'قيد التسليم: {n}',
  'clients.noContract': 'لا عقود سارية',
  'clients.covering': 'سارية: {n}',
  'clients.endingSoon': 'تنتهي خلال الأيام الـ{days} القادمة: {n}',
  'clients.slaNote': 'الالتزام بالمهلة: من التذاكر التي تم فتحها خلال الفترة وأصبحت نتيجة مهلتها محسومة، نسبة ما تمت معالجته في الوقت. وتظهر «غير متاح» عندما لا يوجد شيء محسوم.',
  'clients.moneyNote': 'المحصّل: المدفوعات المستلمة خلال الفترة، بالعملة الأساسية. المستحق: ما تبقّى على فواتيرهم اليوم.',
  'clients.back': 'العملاء',
  'clients.beyondList': 'هذا العميل خارج الحد الذي تقرؤه اللوحة في المرة الواحدة.',
  'clients.service.title': 'سلامة الخدمة',
  'clients.service.subtitle': 'الالتزام بمهل التذاكر التي تم فتحها خلال الفترة.',
  'clients.service.decided': 'تذاكر محسومة',
  'clients.service.satisfaction': 'الرضا',
  'clients.service.noSurvey': 'لا يوجد استبيان لرضا العملاء، لذلك لا نعرض درجة رضا.',
  'clients.money.title': 'المال',
  'clients.contracts.covering': 'سارية اليوم',
  'clients.contracts.endingSoon': 'تنتهي خلال {days} يومًا',
  'clients.detail.projects': 'المشاريع',
  'clients.detail.noProjects': 'لا توجد مشاريع لهذا العميل.',
  'clients.detail.tickets': 'أحدث التذاكر',
  'clients.detail.ticketsHint': 'أحدث 25 تذكرة، أيًّا كانت الفترة.',
  'clients.detail.noTickets': 'لا توجد تذاكر.',
  'clients.detail.invoices': 'الفواتير المفتوحة',
  'clients.detail.invoicesHint': 'صادرة وغير مدفوعة بالكامل، مرتبة حسب تاريخ الاستحقاق (الأقدم أولًا)، وكل منها بعملتها.',
  'clients.detail.noInvoices': 'لا توجد فواتير مفتوحة.',
  'clients.detail.invoice': 'الفاتورة',
  'clients.detail.issued': 'تاريخ الإصدار',
  'clients.detail.due': 'تاريخ الاستحقاق',
  'clients.detail.total': 'الإجمالي',
  'clients.detail.remaining': 'المتبقي',
  'clients.detail.overdue': 'متأخرة',

  /* ---------------------------------------------------------------- Dashboard Access ------- */
  'access.title': 'صلاحية الوصول للوحة',
  'access.subtitle': 'من يمكنه فتح اللوحة التنفيذية.',
  'access.readOnly': 'للقراءة فقط: يتم منح الصلاحية وسحبها في لوحة الإدارة، من «الإعدادات › اللوحة التنفيذية».',
  'access.who': 'لا يدخل إلا مدير نظام نشط لديه «صلاحية الوصول للوحة»، ونتحقق من الشرطين مع كل طلب.',
  'access.empty': 'لا أحد لديه صلاحية الوصول للوحة',
  'access.emptyHint': 'يمكن منحها من لوحة الإدارة: «الإعدادات › اللوحة التنفيذية».',
  'access.col.name': 'الاسم',
  'access.col.jobTitle': 'المسمى الوظيفي',
  'access.col.grantedAt': 'تاريخ المنح',
  'access.col.grantedBy': 'منحها',
  'access.col.lastSignIn': 'آخر تسجيل دخول',
  'access.col.state': 'الحالة',
  'access.grantedBySystem': 'غير مسجّل',
  'access.never': 'أبدًا',
  'access.honoured': 'سارية',
  'access.notHonoured': 'غير سارية',
  'access.notHonouredHint': 'لا تزال مسجّلة، لكنه لم يعد مدير نظام نشطًا، لذلك تنتهي جلسته مع أول طلب يرسله.',

  /* ---------------------------------------------------------------- Dashboard Settings ----- */
  'settings.title': 'إعدادات اللوحة',
  'settings.subtitle': 'الإعدادات السارية اليوم، للقراءة فقط.',
  'settings.defaults': 'القيم الافتراضية — لم يتم حفظ شيء بعد',
  'settings.version': 'النسخة {n}',
  'settings.inForceSince': 'سارية منذ {date}',
  'settings.readOnly': 'للقراءة فقط: يمكن تغييرها من لوحة الإدارة، في «الإعدادات › اللوحة التنفيذية».',
  'settings.versions': 'كل حفظ ينشئ نسخة جديدة تسري من يومها فصاعدًا، ولا نعيد حساب ما صار نهائيًا. والأوزان قيم أولية تقررها الشركة، وليست معيارًا مثبتًا.',
  'settings.on': 'مفعّل',
  'settings.off': 'متوقف',
  'settings.scoring.title': 'درجة العمل',
  'settings.scoring.subtitle': 'أبعادها، وأوزانها، ومتى يظهر الرقم.',
  'settings.scoring.state': 'الحالة',
  'settings.scoring.minEvents': 'أحداث العمل اللازمة لظهور درجة',
  'settings.scoring.minWeight': 'نسبة الوزن التي يجب أن تكون قابلة للقياس',
  'settings.scoring.reviewHours': 'تصبح المراجعة متأخرة بعد (ساعات)',
  'settings.labels.title': 'التصنيفات',
  'settings.labels.subtitle': 'الوصف الذي يظهر مع الدرجة، والرقم يظهر دائمًا أيضًا.',
  'settings.labels.atLeast': '{n} فأكثر',
  'settings.labels.below': 'أقل من {n}',
  'settings.labels.note': 'التصنيف لا يحل محل الرقم أبدًا، ولا نقارن الدرجات بين الأقسام.',
  'settings.overrides.title': 'أوزان الأقسام',
  'settings.overrides.subtitle': 'أوزان خاصة بقسم، نستخدمها للموظفين الذين يكون هذا القسم قسمهم الأساسي.',
  'settings.overrides.none': 'لا يوجد قسم له أوزان خاصة، لذلك نقيّم الجميع بالأوزان أعلاه.',
  'settings.unknownDepartment': 'قسم لم يعد نشطًا',
  'settings.health.title': 'صحة الشركة',
  'settings.health.subtitle': 'أوزان المحاور، ومتى يمكن عرض كل منها.',
  'settings.health.financial': 'المالية',
  'settings.health.work': 'العمل',
  'settings.health.delivery': 'التسليم',
  'settings.health.service': 'الخدمة',
  'settings.health.minWeight': 'نسبة الوزن التي يجب أن تكون قابلة للقياس',
  'settings.health.minScored': 'أشخاص لهم درجة، لمحور العمل',
  'settings.health.minTickets': 'تذاكر، لمحور الخدمة',
  'settings.health.minDatedTasks': 'مهام مفتوحة لها مواعيد نهائية، لمحور التسليم',
  'settings.risks.title': 'حدود المخاطر',
  'settings.risks.subtitle': 'متى تنبّه اللوحة إلى خطر.',
  'settings.risks.blockedQTaskDays': 'تصبح مهمة الطابور معلّقة طويلًا بعد (أيام)',
  'settings.risks.stalledWorkflowDays': 'يصبح سير العمل متعثرًا بعد (أيام)',
  'settings.risks.contractExpiryDays': 'يكون العقد قريب الانتهاء إذا بقي على نهايته (أيام)',
  'settings.risks.overdueReceivableShare': 'نسبة المتأخر من المستحقات',
  'settings.risks.expenseSpike': 'قفزة في المصروفات',
  'settings.risks.receivableConcentration': 'المستحقات على عميل واحد',
  'settings.schedule.title': 'جدول التقارير',
  'settings.schedule.daily': 'التقرير اليومي، بعد انتهاء اليوم',
  'settings.schedule.monthly': 'التقرير الشهري، في أول الشهر',
  'settings.schedule.weekly': 'التقرير الأسبوعي',
  'settings.schedule.settleDays': 'عدد الأيام حتى يصبح التقرير نهائيًا',
  'settings.schedule.note': 'يبقى التقرير مبدئيًا حتى يصبح نهائيًا، وعندها نراجع مصادره من جديد، وأي تغيير فيها ينشئ نسخة جديدة.',
  'settings.retention.title': 'مدة الاحتفاظ',
  'settings.retention.snapshots': 'لقطات التقارير والتقييمات (أشهر)',
  'settings.retention.exports': 'سجل التصدير (أشهر)',
  'settings.retention.forever': 'محفوظة دائمًا',
  'settings.ai.title': 'شروح الذكاء الاصطناعي',
  'settings.ai.subtitle': 'اختيارية: كل الأرقام كاملة بدونها.',
  'settings.ai.switch': 'مفتاح مديري النظام',
  'settings.ai.availability': 'متاح الآن',
  'settings.ai.state.ENABLED': 'متاح',
  'settings.ai.state.DISABLED': 'متوقف',
  'settings.ai.state.NOT_CONFIGURED': 'لم يتم إعداده على الخادم',
  'settings.ai.state.BUDGET_EXCEEDED': 'نفدت ميزانية هذا الشهر',
  'settings.ai.model': 'النموذج المستخدم',
  'settings.ai.noModel': 'لا يوجد',
  'settings.ai.budget': 'الميزانية الشهرية (رموز)',
  'settings.ai.used': 'المستخدم هذا الشهر (رموز)',
  'settings.ai.notCounted': 'لا نحسبه أثناء توقف الذكاء الاصطناعي',
  'settings.ai.reports.daily': 'الملخصات اليومية',
  'settings.ai.reports.weekly': 'الملخصات الأسبوعية',
  'settings.ai.reports.monthly': 'التقارير الشهرية',
  'settings.ai.reports.employees': 'شرح درجات الموظفين',
  'settings.ai.price': 'تقدير السعر لكل مليون رمز',
  'settings.ai.priceValue': 'إدخال {input} · إخراج {output} {currency}',
  'settings.ai.noPrice': 'لم يتم تحديد سعر، فلا يوجد تقدير للتكلفة',
  'settings.ai.priceAsOf': 'الأسعار بتاريخ',
  'settings.ai.note': 'لا يعمل الذكاء الاصطناعي إلا إذا سمح به الخادم وفعّله مديرو النظام، وهو يشرح الأرقام المحفوظة ولا يغيّرها أبدًا.',
  'settings.ai.priceNote': 'أي تكلفة تظهر هنا تقدير بحسب هذه الأسعار المؤرخة، وليست فاتورة فعلية أبدًا.',
};
